import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase/config';
import {
  collection, query, where, onSnapshot, doc, getDoc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { Box, Typography, CircularProgress, Snackbar, Button as MuiButton, IconButton as MuiIconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { QRCodeSVG } from 'qrcode.react';
import {
  fetchDepartures, getLineColor, parseDepartures,
  parseLineLabel, categoryToIcon, getTypeLabel,
} from '../utils/departureApi';
import { PRESET_BACKGROUNDS } from '../constants/castNConstants';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import SubwayIcon from '@mui/icons-material/Subway';
import TramIcon from '@mui/icons-material/Tram';
import TrainIcon from '@mui/icons-material/Train';

// ─── Schedule filter ─────────────────────────────────────────────────────────

function isSlideScheduleActive(slide) {
  const sch = slide?.schedule;
  if (!sch?.enabled) return true; // no schedule = always active

  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10); // YYYY-MM-DD

  // Date range
  if (sch.startDate && todayStr < sch.startDate) return false;
  if (sch.endDate   && todayStr > sch.endDate)   return false;

  // Day of week (JS: 0=Sun ... 6=Sat)
  if (Array.isArray(sch.days) && sch.days.length > 0) {
    if (!sch.days.includes(now.getDay())) return false;
  }

  // Time range
  const nowTime = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  if (sch.startTime && nowTime < sch.startTime) return false;
  if (sch.endTime   && nowTime > sch.endTime)   return false;

  return true;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function pad(n) { return String(n).padStart(2, '0'); }

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const months = ['JAN','FEB','MAR','APR','MAJ','JUN','JUL','AUG','SEP','OKT','NOV','DEC'];
  const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const dateStr = `${now.getDate()} ${months[now.getMonth()]}`;
  return (
    <Typography sx={{ color: '#ccc', fontSize: '1.1rem', fontWeight: 600, letterSpacing: 1 }}>
      {timeStr} / {dateStr}
    </Typography>
  );
}

// ─── Weather helpers ─────────────────────────────────────────────────────────

function smhiWmoFromParam(wsymb) {
  // SMHI symbol_code (samma koder 1-27 som tidigare Wsymb2); map to rough emoji
  const map = {1:'☀️',2:'🌤️',3:'⛅',4:'☁️',5:'🌦️',6:'🌦️',7:'🌫️',
    8:'🌧️',9:'🌧️',10:'🌧️',11:'⛈️',12:'🌨️',13:'❄️',14:'❄️',
    15:'❄️',18:'🌧️',19:'⛈️',20:'🌨️',21:'❄️',25:'🌨️',26:'❄️',27:'❄️'};
  return map[wsymb] || '🌡️';
}

async function fetchSMHIWeather(rawLat, rawLon) {
  // Proxied via Netlify function to avoid CORS issues in browsers
  const lat = parseFloat(rawLat) || 59.33;
  const lon = parseFloat(rawLon) || 18.07;
  const url = `/.netlify/functions/smhi?lat=${lat}&lon=${lon}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`SMHI ${res.status} – koordinaterna kan vara utanför Sverige`);
  const data = await res.json();
  if (!data.timeSeries?.length) throw new Error('SMHI: tomt svar');
  // snow1g: varje tidssteg har ett data-objekt med namngivna fält
  const now = data.timeSeries[0].data || {};
  return {
    temp: Math.round(now.air_temperature ?? 0),
    wsymb: now.symbol_code ?? 1,
    wind: Math.round(now.wind_speed ?? 0),
    precip: Math.round((now.precipitation_amount_max ?? 0) * 10) / 10,
  };
}

// ─── Slide renderers ─────────────────────────────────────────────────────────

const IMG_EFFECTS = {
  none:    { overlay: null,                       filter: 'none' },
  dark:    { overlay: 'rgba(0,0,0,0.5)',          filter: 'none' },
  darker:  { overlay: 'rgba(0,0,0,0.75)',         filter: 'none' },
  orange:  { overlay: 'rgba(255,107,0,0.45)',     filter: 'none' },
  blue:    { overlay: 'rgba(21,101,192,0.5)',     filter: 'none' },
  green:   { overlay: 'rgba(6,78,59,0.5)',        filter: 'none' },
  sepia:   { overlay: 'rgba(120,80,20,0.4)',      filter: 'none' },
  bw:      { overlay: null,                       filter: 'grayscale(100%)' },
};

function TextSlide({ content, compact = false }) {
  const { title, body, image, layout = 'text-only', bgPresetId, bgColor, imgEffect = 'none' } = content || {};
  const hasImg = Boolean(image);
  const bg = bgPresetId
    ? (PRESET_BACKGROUNDS.find(b => b.id === bgPresetId)?.css || '#111')
    : (bgColor || '#111');
  const effect = IMG_EFFECTS[imgEffect] || IMG_EFFECTS.none;
  const isDark = bg !== '#ffffff';
  const tc    = isDark ? '#fff' : '#111';
  const tcsub = isDark ? '#ccc' : '#444';

  // layout: bg-only — image as full background, text overlaid
  if (image && layout === 'bg-only') {
    return (
      <Box sx={{ position:'relative', width:'100%', height:'100%', overflow:'hidden', bgcolor:'#111' }}>
        <img src={image} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', filter: effect.filter }} />
        {effect.overlay && <Box sx={{ position:'absolute', inset:0, background: effect.overlay }} />}
        {(title || body) && !effect.overlay && (
          <Box sx={{ position:'absolute', inset:0, background:'linear-gradient(to top,rgba(0,0,0,0.75) 0%,rgba(0,0,0,0.05) 60%)' }} />
        )}
        {(title || body) && (
          <Box sx={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', justifyContent:'flex-end', alignItems:'center', p:'clamp(1.5rem,4vw,4rem)', gap: 2 }}>
            {title && <Typography fontWeight="bold" sx={{ color:'#fff', textAlign:'center', textShadow:'0 2px 12px rgba(0,0,0,0.9)', fontSize:'clamp(1.4rem,4vw,3.5rem)', lineHeight:1.2 }}>{title}</Typography>}
            {body && <Typography sx={{ color:'rgba(255,255,255,0.9)', textAlign:'center', lineHeight:1.5, textShadow:'0 1px 8px rgba(0,0,0,0.7)', whiteSpace:'pre-wrap', fontSize:'clamp(0.85rem,2vw,1.6rem)' }}>{body}</Typography>}
          </Box>
        )}
      </Box>
    );
  }

  // Side-by-side layouts: image fills its half with cover, text autoscales
  if (hasImg && (layout === 'image-right' || layout === 'image-left')) {
    const imgLeft = layout === 'image-left';
    return (
      <Box sx={{ display:'flex', flexDirection: imgLeft ? 'row-reverse' : 'row', height:'100%', background: bg }}>
        <Box sx={{ width:'55%', position:'relative', overflow:'hidden', flexShrink:0 }}>
          <img src={image} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', filter: effect.filter }} />
          {effect.overlay && <Box sx={{ position:'absolute', inset:0, background: effect.overlay }} />}
        </Box>
        <Box sx={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', p:'clamp(1.5rem,4vw,4rem)', gap: 2, overflow:'hidden' }}>
          {title && <Typography fontWeight="bold" sx={{ color: tc, fontSize:'clamp(1.2rem,3.5vw,2.8rem)', lineHeight:1.2, flexShrink:0 }}>{title}</Typography>}
          {body && <Typography sx={{ color: tcsub, lineHeight:1.6, whiteSpace:'pre-wrap', fontSize:'clamp(0.75rem,1.8vw,1.3rem)', overflow:'hidden', display:'-webkit-box', WebkitBoxOrient:'vertical', WebkitLineClamp:8 }}>{body}</Typography>}
        </Box>
      </Box>
    );
  }

  // Text-only or image-only (column layout)
  const fTitle = compact ? 'clamp(0.75rem,1.5vw,1.1rem)' : 'clamp(1.4rem,4vw,3.5rem)';
  const fBody  = compact ? 'clamp(0.6rem,1vw,0.85rem)'   : 'clamp(0.8rem,2vw,1.5rem)';
  const pad    = compact ? '0.5rem 1rem' : 'clamp(1.5rem,5vw,5rem)';
  return (
    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', background: bg, p: pad, gap: compact ? 1 : 3, overflow:'hidden' }}>
      {hasImg && layout !== 'text-only' && (
        <Box sx={{ width:'100%', flex:'0 0 50%', position:'relative', overflow:'hidden', borderRadius: 2 }}>
          <img src={image} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', filter: effect.filter }} />
          {effect.overlay && <Box sx={{ position:'absolute', inset:0, background: effect.overlay }} />}
        </Box>
      )}
      <Box sx={{ textAlign:'center', flex: hasImg && layout !== 'text-only' ? '0 0 auto' : 'none', maxWidth:'90%' }}>
        {title && <Typography fontWeight="bold" sx={{ color: tc, fontSize: fTitle, lineHeight:1.2, mb: body ? 1 : 0, flexShrink:0 }}>{title}</Typography>}
        {body && <Typography sx={{ color: tcsub, lineHeight:1.5, whiteSpace:'pre-wrap', fontSize: fBody, overflow:'hidden', display:'-webkit-box', WebkitBoxOrient:'vertical', WebkitLineClamp: compact ? 4 : 10 }}>{body}</Typography>}
      </Box>
    </Box>
  );
}

function MediaSlide({ content }) {
  const { url, fileType, displayMode = 'zone-cover' } = content || {};
  const fit = (displayMode === 'zone-contain' || displayMode === 'fullscreen-contain') ? 'contain' : 'cover';
  if (!url) return <Box sx={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100%' }}><Typography color="#666">Ingen media</Typography></Box>;
  if (fileType === 'pdf') {
    return <iframe src={url} title="PDF" style={{ width:'100%', height:'100%', border:'none', background:'#fff' }} />;
  }
  if (['mp4','mov','webm'].some(ext => (fileType||'').includes(ext) || url.toLowerCase().includes(ext))) {
    return <video src={url} autoPlay muted loop style={{ width:'100%', height:'100%', objectFit:'contain', background:'#000' }} />;
  }
  return <img src={url} alt="" style={{ width:'100%', height:'100%', objectFit: fit, background:'#000' }} />;
}

function ImageUrlSlide({ content }) {
  const { url } = content || {};
  return <img src={url} alt="" style={{ objectFit:'contain', width:'100%', height:'100%', background:'#000' }} />;
}

function VideoSlide({ content }) {
  const { url, youtubeId } = content || {};
  if (youtubeId) {
    return <iframe src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}`}
      title="YouTube" style={{ width:'100%', height:'100%', border:'none' }} allow="autoplay" />;
  }
  if (url) {
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([A-Za-z0-9_-]{11})/);
    if (ytMatch) {
      const id = ytMatch[1];
      return <iframe src={`https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}`}
        title="YouTube" style={{ width:'100%', height:'100%', border:'none' }} allow="autoplay" />;
    }
    return <video src={url} autoPlay muted loop style={{ width:'100%', height:'100%', objectFit:'contain' }} />;
  }
  return null;
}

function WeatherSlide({ content, compact = false }) {
  const lat = parseFloat(content?.lat) || 59.33;
  const lon = parseFloat(content?.lon) || 18.07;
  const city = content?.city || 'Stockholm';
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setWeather(null); setError(null);
    fetchSMHIWeather(lat, lon)
      .then(setWeather)
      .catch(e => setError(e.message));
  }, [lat, lon]);
  if (error) return (
    <Box sx={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100%',flexDirection:'column',gap:1, p:2 }}>
      <Typography sx={{ color:'#f66', fontSize:'clamp(0.7rem,1.5vw,1rem)', textAlign:'center' }}>⚠️ Väder ej tillgängligt</Typography>
      <Typography sx={{ color:'#555', fontSize:'clamp(0.55rem,1vw,0.7rem)', textAlign:'center', maxWidth:300 }}>{error}</Typography>
    </Box>
  );
  if (!weather) return <Box sx={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100%' }}><CircularProgress sx={{ color:'#FF6B00' }} /></Box>;

  if (compact) {
    return (
      <Box sx={{ display:'flex', flexDirection:'row', alignItems:'center', justifyContent:'center', height:'100%', gap: 2, px: 2 }}>
        <Typography sx={{ fontSize:'clamp(1.5rem,4vw,3rem)', lineHeight:1 }}>{smhiWmoFromParam(weather.wsymb)}</Typography>
        <Box>
          <Typography fontWeight="bold" sx={{ color:'#fff', fontSize:'clamp(1.2rem,4vw,2.5rem)', lineHeight:1 }}>{weather.temp}°</Typography>
          <Typography sx={{ color:'#aaa', fontSize:'clamp(0.6rem,1.2vw,0.85rem)' }}>{city}</Typography>
        </Box>
        <Box sx={{ display:'flex', flexDirection:'column', gap: 0.25 }}>
          <Typography sx={{ color:'#ccc', fontSize:'clamp(0.55rem,1vw,0.8rem)' }}>💨 {weather.wind} m/s</Typography>
          <Typography sx={{ color:'#ccc', fontSize:'clamp(0.55rem,1vw,0.8rem)' }}>🌧️ {weather.precip} mm</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap: 2, p: 2 }}>
      <Typography sx={{ fontSize: 'clamp(2.5rem,8vw,6rem)', lineHeight:1 }}>{smhiWmoFromParam(weather.wsymb)}</Typography>
      <Typography fontWeight="bold" sx={{ color:'#fff', fontSize:'clamp(2.5rem,10vw,8rem)', lineHeight:1 }}>{weather.temp}°</Typography>
      <Typography sx={{ color:'#aaa', fontSize:'clamp(0.85rem,2.5vw,1.5rem)' }}>{city}</Typography>
      <Box sx={{ display:'flex', gap: 3, flexWrap:'wrap', justifyContent:'center' }}>
        <Typography sx={{ color:'#ccc', fontSize:'clamp(0.75rem,2vw,1.4rem)' }}>💨 {weather.wind} m/s</Typography>
        <Typography sx={{ color:'#ccc', fontSize:'clamp(0.75rem,2vw,1.4rem)' }}>🌧️ {weather.precip} mm</Typography>
      </Box>
    </Box>
  );
}

function WeatherMapSlide({ content }) {
  const { lat = 59.33, lon = 18.07, zoom = 5, layer = 'wind' } = content || {};
  const url = `https://embed.windy.com/embed2.html?lat=${lat}&lon=${lon}&detailLat=${lat}&detailLon=${lon}&width=650&height=450&zoom=${zoom}&level=surface&overlay=${layer}&product=ecmwf&menu=&message=true&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=m%2Fs&metricTemp=%C2%B0C&radarRange=-1`;
  return <iframe src={url} title="Weather Map" style={{ width:'100%', height:'100%', border:'none' }} />;
}

function getModeIcon(icon) {
  if (icon === 'metro') return <SubwayIcon sx={{ fontSize: '1.1rem' }} />;
  if (icon === 'tram') return <TramIcon sx={{ fontSize: '1.1rem' }} />;
  if (icon === 'train') return <TrainIcon sx={{ fontSize: '1.1rem' }} />;
  return <DirectionsBusIcon sx={{ fontSize: '1.1rem' }} />;
}

function DeptRow({ group }) {
  const { line, trainType } = parseLineLabel(group.lineNumber);
  const typeLabel = trainType || getTypeLabel(group.category);
  const lineColor = getLineColor(line, group.category);
  const modeIcon = getModeIcon(categoryToIcon(group.category));
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, minWidth: 0 }}>
      <Box sx={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center', color: '#475569' }}>
        {modeIcon}
      </Box>
      <Box sx={{ width: 90, height: 26, borderRadius: '4px', bgcolor: '#334155',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Typography sx={{ color: '#cbd5e1', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {typeLabel}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 34, height: 26, px: 0.8, borderRadius: '4px', bgcolor: lineColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 800 }}>{line}</Typography>
      </Box>
      <Typography sx={{ flex: 1, fontSize: '0.95rem', color: '#e2e8f0',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {group.direction}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        {group.departures.slice(0, 3).map((m, i) => (
          <Box key={i} sx={{
            display: 'flex', alignItems: 'center', gap: '1px',
            bgcolor: m <= 0 ? '#DC2626' : '#1e293b',
            borderRadius: '4px', px: 0.8, height: 28, color: '#fff',
          }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
              {m <= 0 ? 'Nu' : m}
            </Typography>
            {m > 0 && (
              <Typography sx={{ fontSize: '0.6rem', opacity: 0.7, lineHeight: 1, ml: '1px' }}>min</Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function StopBlock({ stop }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!stop?.id) { setLoading(false); return; }
    const load = async () => {
      try {
        const raw = await fetchDepartures(stop.id, 20);
        setGroups(parseDepartures(raw).slice(0, 8));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [stop?.id]);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ bgcolor: '#1e293b', borderRadius: '6px 6px 0 0', px: 2, py: 1.25, mb: 0.5, borderLeft: '4px solid #FF6B00' }}>
        <Typography sx={{ color: '#FF6B00', fontSize: 'clamp(0.9rem,2vw,1.2rem)', fontWeight: 800, letterSpacing: 0.5 }}>
          {stop.name}
        </Typography>
      </Box>
      <Box sx={{ px: 1 }}>
        {loading ? (
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={20} sx={{ color: '#FF6B00' }} />
          </Box>
        ) : groups.length === 0 ? (
          <Typography sx={{ color: '#475569', fontSize: '0.85rem', py: 1 }}>Inga avgångar hittades</Typography>
        ) : (
          groups.map((g, i) => <DeptRow key={i} group={g} />)
        )}
      </Box>
    </Box>
  );
}

function TrafficSlide({ content }) {
  const stops = content?.stops || (content?.stopId ? [{ id: content.stopId, name: content.stopName || 'Avgångar', icon: 'bus' }] : []);
  const title = content?.title || null;

  if (!stops.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ color: '#475569', fontSize: '1.2rem' }}>Ingen hållplats konfigurerad</Typography>
        <Typography sx={{ color: '#334155', fontSize: '0.9rem' }}>Lägg till hållplatser i CastN-inställningar</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#0f172a', p: 3, overflow: 'hidden' }}>
      {title && (
        <Typography sx={{ color: '#FF6B00', fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>{title}</Typography>
      )}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {stops.map(stop => <StopBlock key={stop.id} stop={stop} />)}
      </Box>
    </Box>
  );
}

function RSSSlide({ content, compact = false }) {
  const { url, title = 'Nyheter' } = content || {};
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!url) return;
    fetch(`/.netlify/functions/rss?url=${encodeURIComponent(url)}`)
      .then(r => r.json())
      .then(data => setItems(data.items || []))
      .catch(console.error);
  }, [url]);
  useEffect(() => {
    if (!items.length || compact) return;
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items, compact]);

  if (!items.length) return (
    <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%' }}>
      <CircularProgress sx={{ color:'#FF6B00' }} />
    </Box>
  );

  // Compact mode: horizontal scrolling ticker
  if (compact) {
    const tickerText = items.map(it => it.title).join('   ●   ');
    const doubled = tickerText + '   ●   ' + tickerText;
    const duration = Math.max(20, items.length * 8);
    return (
      <Box sx={{ height:'100%', display:'flex', alignItems:'center', overflow:'hidden', gap: 1.5, px: 1 }}>
        <Typography sx={{ color:'#FF6B00', fontWeight:800, fontSize:'clamp(0.55rem,1vw,0.75rem)',
          textTransform:'uppercase', letterSpacing:1.5, flexShrink:0, whiteSpace:'nowrap' }}>
          {title}
        </Typography>
        <Box sx={{ flex:1, overflow:'hidden', position:'relative' }}>
          <Box sx={{
            display:'inline-block', whiteSpace:'nowrap',
            '@keyframes rss-ticker': {
              '0%':   { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-50%)' },
            },
            animation: `rss-ticker ${duration}s linear infinite`,
          }}>
            <Typography component="span" sx={{ fontSize:'clamp(0.65rem,1.2vw,0.9rem)', color:'#e2e8f0' }}>
              {doubled}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const item = items[idx];
  const rawDesc = item.description?.replace(/<[^>]*>/g, '').trim() || '';
  const looksLikeUrlMeta = /^(Article URL:|Comments URL:|http)/i.test(rawDesc);
  const desc = looksLikeUrlMeta ? '' : rawDesc.substring(0, 220);

  return (
    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      height:'100%', p: 'clamp(1rem,4vw,3.5rem)', gap: 'clamp(0.75rem,2vw,2rem)' }}>
      <Typography sx={{ color:'#FF6B00', textTransform:'uppercase', letterSpacing: 2,
        fontSize: 'clamp(0.65rem,1.5vw,1.1rem)' }}>{title}</Typography>
      <Typography fontWeight="bold" sx={{ color:'#fff', textAlign:'center', lineHeight: 1.3,
        fontSize: 'clamp(1rem,3vw,2rem)', overflow:'hidden', display:'-webkit-box',
        WebkitBoxOrient:'vertical', WebkitLineClamp: 6 }}>{item.title}</Typography>
      {desc && (
        <Typography sx={{ color:'#aaa', textAlign:'center', maxWidth:'80%',
          fontSize: 'clamp(0.75rem,1.8vw,1.1rem)', lineHeight: 1.5 }}>
          {desc}{desc.length >= 220 ? '…' : ''}
        </Typography>
      )}
      <Typography sx={{ color:'#444', fontSize: 'clamp(0.6rem,1.2vw,0.85rem)' }}>{idx+1} / {items.length}</Typography>
    </Box>
  );
}

function UrlSlide({ content }) {
  const { url } = content || {};
  return <iframe src={url} width="100%" height="100%" frameBorder="0" title="URL" style={{ border:'none' }} />;
}

function AnalogClock({ tz, variant = 'modern', size = 160 }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Get time in target timezone via locale
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: tz, hour:'numeric', minute:'numeric', second:'numeric', hour12: false
  }).formatToParts(now);
  const get = (type) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
  const h = get('hour') % 12;
  const m = get('minute');
  const s = get('second');

  const hourDeg   = (h / 12) * 360 + (m / 60) * 30;
  const minuteDeg = (m / 60) * 360 + (s / 60) * 6;
  const secondDeg = (s / 60) * 360;

  const cx = size / 2;
  const r  = size / 2 - 4;

  const isModern = variant === 'analog-modern';
  const faceColor    = isModern ? '#1e293b' : '#f8f0dc';
  const rimColor     = isModern ? '#FF6B00' : '#8B4513';
  const hourColor    = isModern ? '#fff' : '#1a1a1a';
  const minuteColor  = isModern ? '#ccc' : '#1a1a1a';
  const secondColor  = isModern ? '#FF6B00' : '#cc2200';
  const markerColor  = isModern ? '#475569' : '#8B4513';
  const textColor    = isModern ? '#94a3b8' : '#8B4513';

  const hand = (deg, len, width, color) => {
    const rad = (deg - 90) * (Math.PI / 180);
    const x2 = cx + Math.cos(rad) * len;
    const y2 = cx + Math.sin(rad) * len;
    return <line x1={cx} y1={cx} x2={x2} y2={y2} stroke={color} strokeWidth={width} strokeLinecap="round" />;
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Face */}
      <circle cx={cx} cy={cx} r={r} fill={faceColor} stroke={rimColor} strokeWidth={isModern ? 3 : 4} />
      {/* Hour markers */}
      {Array.from({length:12},(_,i)=>{
        const a = (i/12)*360 - 90;
        const rad = a * Math.PI/180;
        const r1 = r - (i%3===0 ? 10 : 5);
        const r2 = r - 1;
        return <line key={i}
          x1={cx+Math.cos(rad)*r1} y1={cx+Math.sin(rad)*r1}
          x2={cx+Math.cos(rad)*r2} y2={cx+Math.sin(rad)*r2}
          stroke={markerColor} strokeWidth={i%3===0 ? 2.5 : 1.2} />;
      })}
      {/* Hour numbers for classic */}
      {!isModern && [12,3,6,9].map((n,i)=>{
        const a = (i/4)*360 - 90;
        const rad = a * Math.PI/180;
        const pr = r - 18;
        return <text key={n} x={cx+Math.cos(rad)*pr} y={cx+Math.sin(rad)*pr}
          textAnchor="middle" dominantBaseline="middle" fill={textColor} fontSize={size*0.09} fontWeight="bold">{n}</text>;
      })}
      {/* Hands */}
      {hand(hourDeg,   r*0.52, isModern?5:4, hourColor)}
      {hand(minuteDeg, r*0.72, isModern?3:3, minuteColor)}
      {hand(secondDeg, r*0.78, isModern?1.5:1.5, secondColor)}
      {/* Center dot */}
      <circle cx={cx} cy={cx} r={isModern?4:3} fill={secondColor} />
    </svg>
  );
}

function WorldClockSlide({ content }) {
  const { zones = [
    { label: 'Stockholm', tz: 'Europe/Stockholm' },
    { label: 'London', tz: 'Europe/London' },
    { label: 'New York', tz: 'America/New_York' },
    { label: 'Tokyo', tz: 'Asia/Tokyo' },
  ], clockType = 'digital' } = content || {};
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const isAnalog = clockType === 'analog-modern' || clockType === 'analog-classic';
  return (
    <Box sx={{ display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'center', gap: 6, height:'100%', p: 4 }}>
      {zones.map((z, i) => {
        const timeStr = now.toLocaleTimeString('sv-SE', { timeZone: z.tz, hour:'2-digit', minute:'2-digit' });
        const dateStr = now.toLocaleDateString('sv-SE', { timeZone: z.tz, day:'numeric', month:'short' });
        return (
          <Box key={i} sx={{ textAlign:'center', minWidth: isAnalog ? 180 : 160 }}>
            <Typography sx={{ color:'#FF6B00', fontSize:'1rem', letterSpacing: 2, textTransform:'uppercase', mb: 1 }}>{z.label}</Typography>
            {isAnalog ? (
              <Box sx={{ display:'flex', justifyContent:'center', mb: 1 }}>
                <AnalogClock tz={z.tz} variant={clockType} size={160} />
              </Box>
            ) : (
              <Typography sx={{ color:'#fff', fontSize:'4rem', fontWeight:'bold', fontVariantNumeric:'tabular-nums' }}>{timeStr}</Typography>
            )}
            <Typography sx={{ color:'#777', fontSize:'1rem' }}>{dateStr}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function MenuSlide({ content }) {
  const { title = 'Veckans meny', days = [] } = content || {};
  return (
    <Box sx={{ height:'100%', display:'flex', flexDirection:'column', p: 5 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ color:'#FF6B00', mb: 3 }}>{title}</Typography>
      <Box sx={{ display:'flex', flexWrap:'wrap', gap: 2, flex: 1 }}>
        {days.map((day, i) => (
          <Box key={i} sx={{ flex:'1 1 180px', bgcolor:'#1e1e1e', borderRadius: 2, p: 2, borderTop:'3px solid #FF6B00' }}>
            <Typography fontWeight="bold" sx={{ color:'#FF6B00', mb: 1 }}>{day.label || `Dag ${i+1}`}</Typography>
            <Typography sx={{ color:'#ccc', whiteSpace:'pre-wrap', fontSize:'0.95rem' }}>{day.menu}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function LagbasnReferenceSlide({ content }) {
  const { projectId, layout = 'image-left', imgFit = 'cover', textColor = 'auto' } = content || {};
  const [project, setProject] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    getDoc(doc(db, 'referenceProjects', projectId)).then(snap => {
      if (snap.exists()) setProject({ id: snap.id, ...snap.data() });
    });
  }, [projectId]);
  if (!project) return <Box sx={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100%' }}><CircularProgress sx={{ color:'#FF6B00' }} /></Box>;
  const startYear = project.startDate ? new Date(project.startDate).getFullYear() : null;
  const endYear   = project.endDate   ? new Date(project.endDate).getFullYear()   : null;
  const period = startYear && !isNaN(startYear)
    ? `${startYear}–${endYear && !isNaN(endYear) ? endYear : 'pågående'}`
    : '';
  const tc = textColor === 'white' ? '#fff' : textColor === 'dark' ? '#111' : '#fff';
  const tcsub = textColor === 'dark' ? '#444' : '#ccc';

  const TextBlock = () => (
    <Box sx={{ display:'flex', flexDirection:'column', justifyContent:'center', p: 'clamp(1.5rem,4vw,4rem)', gap: 1.5, height:'100%', overflow:'hidden' }}>
      <Typography sx={{ color:'#FF6B00', fontSize:'clamp(0.65rem,1.2vw,0.9rem)', letterSpacing: 3, textTransform:'uppercase', flexShrink:0 }}>Referens</Typography>
      <Typography fontWeight="bold" sx={{ color: tc, lineHeight: 1.15, fontSize:'clamp(1.1rem,3.5vw,2.8rem)', flexShrink:0 }}>{project.name}</Typography>
      {project.client && (
        <Typography sx={{ color: tcsub, fontSize:'clamp(0.85rem,2vw,1.4rem)', fontWeight:600, flexShrink:0 }}>{project.client}</Typography>
      )}
      <Box sx={{ display:'flex', gap: 2, flexWrap:'wrap', flexShrink:0 }}>
        {period && <Typography sx={{ color:'#FF6B00', fontSize:'clamp(0.75rem,1.5vw,1rem)', fontWeight:700 }}>{period}</Typography>}
        {project.location && <Typography sx={{ color:'#666', fontSize:'clamp(0.75rem,1.5vw,1rem)' }}>📍 {project.location}</Typography>}
        {project.contractValue && <Typography sx={{ color:'#666', fontSize:'clamp(0.75rem,1.5vw,1rem)' }}>💰 {Number(project.contractValue).toLocaleString('sv-SE')} kr</Typography>}
      </Box>
      {project.projectDescription && (
        <Typography sx={{ color: tcsub, fontSize:'clamp(0.75rem,1.6vw,1.05rem)', lineHeight: 1.65, mt: 0.5,
          overflow:'hidden', display:'-webkit-box', WebkitBoxOrient:'vertical', WebkitLineClamp: layout === 'text-only' ? 12 : 7 }}>
          {project.projectDescription}
        </Typography>
      )}
      {project.tags?.length > 0 && (
        <Box sx={{ display:'flex', gap: 1, flexWrap:'wrap', mt: 0.5, flexShrink:0 }}>
          {project.tags.slice(0,5).map((t,i) => (
            <Box key={i} sx={{ bgcolor:'rgba(255,107,0,0.15)', color:'#FF6B00', borderRadius:1, px:1, py:0.25, fontSize:'clamp(0.6rem,1.1vw,0.8rem)', fontWeight:600 }}>{t}</Box>
          ))}
        </Box>
      )}
    </Box>
  );

  const ImgBox = ({ style: imgStyle = {} }) => (
    project.image
      ? <img src={project.image} alt={project.name}
          style={{ width:'100%', height:'100%', objectFit: imgFit, background:'#111', ...imgStyle }} />
      : <Box sx={{ width:'100%', height:'100%', bgcolor:'#111', display:'flex',alignItems:'center',justifyContent:'center' }}>
          <Typography sx={{ color:'#333', fontSize:'5rem' }}>🏗️</Typography>
        </Box>
  );

  if (layout === 'image-bg') {
    return (
      <Box sx={{ position:'relative', width:'100%', height:'100%', overflow:'hidden' }}>
        <ImgBox style={{ position:'absolute', inset:0 }} />
        <Box sx={{ position:'absolute', inset:0, background:'linear-gradient(to right,rgba(0,0,0,0.85) 45%,rgba(0,0,0,0.2) 100%)' }} />
        <Box sx={{ position:'absolute', inset:0 }}><TextBlock /></Box>
      </Box>
    );
  }
  if (layout === 'image-top') {
    return (
      <Box sx={{ display:'flex', flexDirection:'column', height:'100%' }}>
        <Box sx={{ flex:'0 0 55%', overflow:'hidden' }}><ImgBox /></Box>
        <Box sx={{ flex:'1 1 45%', bgcolor:'#1a1a1a' }}><TextBlock /></Box>
      </Box>
    );
  }
  if (layout === 'text-only') {
    return <Box sx={{ height:'100%', bgcolor:'#1a1a1a' }}><TextBlock /></Box>;
  }
  const imgFirst = layout !== 'image-right';
  return (
    <Box sx={{ display:'flex', height:'100%' }}>
      {imgFirst && <Box sx={{ width:'55%', position:'relative', overflow:'hidden' }}><ImgBox /></Box>}
      <Box sx={{ flex: 1, bgcolor:'#1a1a1a' }}><TextBlock /></Box>
      {!imgFirst && <Box sx={{ width:'55%', position:'relative', overflow:'hidden' }}><ImgBox /></Box>}
    </Box>
  );
}

function LagbasnLeadsSlide({ content }) {
  const [tenders, setTenders] = useState([]);
  useEffect(() => {
    const q = query(collection(db, 'tenders'), where('status', 'in', ['active', 'submitted']));
    const unsub = onSnapshot(q, snap => {
      setTenders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);
  return (
    <Box sx={{ height:'100%', display:'flex', flexDirection:'column', p: 4 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ color:'#FF6B00', mb: 3 }}>Aktiva anbud</Typography>
      <Box sx={{ display:'flex', flexWrap:'wrap', gap: 2, flex: 1, alignContent:'flex-start' }}>
        {tenders.map(t => (
          <Box key={t.id} sx={{ flex:'1 1 280px', bgcolor:'#1e1e1e', borderRadius: 2, p: 2.5,
            borderLeft:`4px solid ${t.status==='submitted'?'#4caf50':'#FF6B00'}` }}>
            <Typography fontWeight="bold" sx={{ color:'#fff', fontSize:'1.1rem', mb: 0.5 }}>{t.title || t.name || 'Anbud'}</Typography>
            {t.client && <Typography sx={{ color:'#aaa', fontSize:'0.9rem' }}>{t.client}</Typography>}
            <Typography sx={{ color: t.status==='submitted'?'#4caf50':'#FF6B00', fontSize:'0.85rem', mt: 1, fontWeight:'bold' }}>
              {t.status === 'submitted' ? 'INLÄMNAT' : 'AKTIVT'}
            </Typography>
          </Box>
        ))}
        {tenders.length === 0 && <Typography sx={{ color:'#555' }}>Inga aktiva anbud</Typography>}
      </Box>
    </Box>
  );
}

function useConfetti() {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);

  const launch = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const colors = ['#FF6B00','#FFD700','#00C853','#2196F3','#E91E63'];
    for (let i = 0; i < 120; i++) {
      particlesRef.current.push({
        x: canvas.width * Math.random(),
        y: -10,
        vx: (Math.random()-0.5)*6,
        vy: Math.random()*4+2,
        color: colors[Math.floor(Math.random()*colors.length)],
        size: Math.random()*8+4,
        life: 1,
      });
    }
    const animate = () => {
      ctx.clearRect(0,0,canvas.width,canvas.height);
      particlesRef.current = particlesRef.current.filter(p => p.life > 0);
      particlesRef.current.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.1; p.life -= 0.008;
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x, p.y, p.size, p.size);
      });
      ctx.globalAlpha = 1;
      if (particlesRef.current.length > 0) rafRef.current = requestAnimationFrame(animate);
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);
  return { canvasRef, launch };
}

function LagbasnWinsSlide({ content }) {
  const { period } = content || {};
  const [tenders, setTenders] = useState([]);
  const { canvasRef, launch } = useConfetti();
  const prevIds = useRef(new Set());

  useEffect(() => {
    const q = query(collection(db, 'tenders'), where('status', '==', 'won'));
    const unsub = onSnapshot(q, snap => {
      let docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (period) {
        const now = new Date();
        const year = now.getFullYear();
        docs = docs.filter(t => {
          const d = t.wonAt?.toDate?.() || t.updatedAt?.toDate?.() || new Date(t.updatedAt || 0);
          if (period === 'year') return d.getFullYear() === year;
          if (period === 'quarter') return d.getFullYear() === year && Math.floor(d.getMonth()/3) === Math.floor(now.getMonth()/3);
          if (period === 'month') return d.getFullYear() === year && d.getMonth() === now.getMonth();
          return true;
        });
      }
      // detect new wins
      const newIds = new Set(docs.map(d => d.id));
      const hasNew = [...newIds].some(id => !prevIds.current.has(id)) && prevIds.current.size > 0;
      if (hasNew) launch();
      prevIds.current = newIds;
      setTenders(docs);
    });
    return unsub;
  }, [period, launch]);

  return (
    <Box sx={{ height:'100%', display:'flex', flexDirection:'column', p: 4, position:'relative', overflow:'hidden' }}>
      <canvas ref={canvasRef} style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:10 }} />
      <Typography variant="h4" fontWeight="bold" sx={{ color:'#FFD700', mb: 1 }}>🏆 Vunna affärer</Typography>
      <Typography sx={{ color:'#aaa', mb: 3, fontSize:'0.9rem' }}>
        {period === 'month' ? 'Denna månad' : period === 'quarter' ? 'Detta kvartal' : period === 'year' ? 'I år' : 'Alla'}
      </Typography>
      <Box sx={{ display:'flex', flexWrap:'wrap', gap: 2, flex: 1, alignContent:'flex-start' }}>
        {tenders.map(t => (
          <Box key={t.id} sx={{ flex:'1 1 280px', bgcolor:'#1e1e1e', borderRadius: 2, p: 2.5, borderLeft:'4px solid #FFD700' }}>
            <Typography fontWeight="bold" sx={{ color:'#fff', fontSize:'1.1rem', mb: 0.5 }}>{t.title || t.name || 'Affär'}</Typography>
            {t.client && <Typography sx={{ color:'#aaa', fontSize:'0.9rem' }}>{t.client}</Typography>}
            {t.budget && <Typography sx={{ color:'#FFD700', fontWeight:'bold', mt: 1 }}>{Number(t.budget).toLocaleString('sv-SE')} kr</Typography>}
          </Box>
        ))}
        {tenders.length === 0 && <Typography sx={{ color:'#555' }}>Inga vunna affärer ännu</Typography>}
      </Box>
    </Box>
  );
}

// ─── New slide type renderers ─────────────────────────────────────────────────

function EmojiSlide({ content }) {
  const { emoji = '🎉', caption, size = 'xl', bgPresetId = 'grad-night' } = content || {};
  const bg = PRESET_BACKGROUNDS.find(b => b.id === bgPresetId)?.css || '#1e293b';
  const fontSize = size === 'xxl' ? '18rem' : size === 'xl' ? '12rem' : '8rem';
  return (
    <Box sx={{ width:'100%', height:'100%', background: bg, display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', gap: 3 }}>
      <Typography sx={{ fontSize, lineHeight: 1, userSelect:'none' }}>{emoji}</Typography>
      {caption && (
        <Typography sx={{ color:'#fff', fontSize:'2.5rem', fontWeight: 700, textAlign:'center',
          px: 6, textShadow:'0 2px 16px rgba(0,0,0,0.6)' }}>{caption}</Typography>
      )}
    </Box>
  );
}

function EmbedSlide({ content }) {
  const { embedUrl } = content || {};
  if (!embedUrl) return (
    <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', bgcolor:'#111' }}>
      <Typography sx={{ color:'#555' }}>Ingen URL konfigurerad</Typography>
    </Box>
  );
  return <iframe src={embedUrl} title="embed" style={{ width:'100%', height:'100%', border:'none', background:'#fff' }} />;
}

function FacebookSlide({ content }) {
  const { pageUrl = '', tabs = 'timeline' } = content || {};
  if (!pageUrl) return (
    <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', bgcolor:'#1877F2' }}>
      <Typography sx={{ color:'white', fontSize:'2rem' }}>Facebook – ingen sida konfigurerad</Typography>
    </Box>
  );
  const src = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(pageUrl)}&tabs=${tabs}&width=500&height=700&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=true`;
  return (
    <Box sx={{ width:'100%', height:'100%', bgcolor:'#1877F2', display:'flex', flexDirection:'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor:'#1877F2', px: 3, py: 1.5, display:'flex', alignItems:'center', gap: 1.5, flexShrink: 0 }}>
        <Typography sx={{ color:'white', fontSize:'1.5rem' }}>📘</Typography>
        <Typography sx={{ color:'white', fontWeight: 700, fontSize:'1.1rem' }}>Facebook</Typography>
      </Box>
      <Box sx={{ flex: 1, overflow:'hidden', bgcolor:'#f0f2f5' }}>
        <iframe src={src} title="Facebook Page" scrolling="no"
          style={{ width:'100%', height:'100%', border:'none', overflow:'hidden' }}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" />
      </Box>
    </Box>
  );
}

function InstagramSlide({ content }) {
  const { username = '', displayMode = 'grid' } = content || {};
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!username) { setLoading(false); return; }
    const rssUrl = `https://rsshub.app/instagram/user/${username}`;
    fetch(`/.netlify/functions/rss?url=${encodeURIComponent(rssUrl)}`)
      .then(r => r.json())
      .then(data => { setPosts(data.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [username]);

  const igGradient = 'linear-gradient(45deg,#f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)';

  if (!username) return (
    <Box sx={{ width:'100%', height:'100%', background: igGradient, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Typography sx={{ color:'white', fontSize:'1.5rem' }}>Instagram – inget konto konfigurerat</Typography>
    </Box>
  );

  return (
    <Box sx={{ width:'100%', height:'100%', bgcolor:'#000', display:'flex', flexDirection:'column' }}>
      {/* Header */}
      <Box sx={{ background: igGradient, px: 3, py: 1.5, display:'flex', alignItems:'center', gap: 1.5, flexShrink: 0 }}>
        <Typography sx={{ color:'white', fontSize:'1.5rem' }}>📸</Typography>
        <Typography sx={{ color:'white', fontWeight: 700, fontSize:'1.2rem' }}>@{username}</Typography>
        <Typography sx={{ color:'rgba(255,255,255,0.7)', fontSize:'0.9rem', ml: 'auto' }}>Instagram</Typography>
      </Box>

      {loading ? (
        <Box sx={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <CircularProgress sx={{ color:'#e6683c' }} />
        </Box>
      ) : posts.length === 0 ? (
        <Box sx={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Typography sx={{ color:'#555' }}>Inga bilder hittades. Kontrollera att kontot är publikt.</Typography>
        </Box>
      ) : displayMode === 'latest' ? (
        /* Latest single post big */
        <Box sx={{ flex:1, position:'relative', overflow:'hidden' }}>
          {posts[0]?.thumbnail && (
            <img src={posts[0].thumbnail} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          )}
          <Box sx={{ position:'absolute', bottom:0, left:0, right:0, p: 3,
            background:'linear-gradient(to top,rgba(0,0,0,0.8),transparent)' }}>
            <Typography sx={{ color:'white', fontSize:'1.1rem' }}
              dangerouslySetInnerHTML={{ __html: posts[0]?.description?.replace(/<[^>]+>/g,'').slice(0,160) || '' }} />
          </Box>
        </Box>
      ) : (
        /* Grid */
        <Box sx={{ flex:1, display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:0.5, p: 0.5, overflow:'hidden' }}>
          {posts.slice(0,6).map((p, i) => (
            <Box key={i} sx={{ position:'relative', aspectRatio:'1', overflow:'hidden', bgcolor:'#111' }}>
              {p.thumbnail
                ? <img src={p.thumbnail} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : <Box sx={{ width:'100%', height:'100%', bgcolor:'#222', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Typography sx={{ color:'#555', fontSize:'0.7rem' }}>📸</Typography>
                  </Box>
              }
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function LinkedInSlide({ content }) {
  const { companySlug = '', count = 5 } = content || {};
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companySlug) { setLoading(false); return; }
    const rssUrl = `https://rsshub.app/linkedin/company/${companySlug}`;
    fetch(`/.netlify/functions/rss?url=${encodeURIComponent(rssUrl)}`)
      .then(r => r.json())
      .then(data => { setPosts(data.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [companySlug, count]);

  if (!companySlug) return (
    <Box sx={{ width:'100%', height:'100%', bgcolor:'#0077B5', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Typography sx={{ color:'white', fontSize:'1.5rem' }}>LinkedIn – inget företag konfigurerat</Typography>
    </Box>
  );

  return (
    <Box sx={{ width:'100%', height:'100%', bgcolor:'#f3f2ef', display:'flex', flexDirection:'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor:'#0077B5', px: 3, py: 1.5, display:'flex', alignItems:'center', gap: 1.5, flexShrink: 0 }}>
        <Typography sx={{ color:'white', fontSize:'1.5rem' }}>💼</Typography>
        <Typography sx={{ color:'white', fontWeight: 700, fontSize:'1.1rem' }}>LinkedIn</Typography>
        <Typography sx={{ color:'rgba(255,255,255,0.8)', fontSize:'0.95rem', ml: 0.5 }}>– {companySlug}</Typography>
      </Box>

      {loading ? (
        <Box sx={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <CircularProgress sx={{ color:'#0077B5' }} />
        </Box>
      ) : posts.length === 0 ? (
        <Box sx={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Typography sx={{ color:'#888' }}>Inga inlägg hittades.</Typography>
        </Box>
      ) : (
        <Box sx={{ flex:1, overflowY:'hidden', display:'flex', flexDirection:'column', gap: 0, p: 2 }}>
          {posts.slice(0, count).map((p, i) => (
            <Box key={i} sx={{ bgcolor:'white', borderRadius: 2, p: 2, mb: 1.5,
              boxShadow:'0 1px 4px rgba(0,0,0,0.1)', display:'flex', gap: 2 }}>
              {p.thumbnail && (
                <Box sx={{ width: 80, height: 60, flexShrink:0, borderRadius: 1, overflow:'hidden' }}>
                  <img src={p.thumbnail} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                </Box>
              )}
              <Box sx={{ flex:1, minWidth:0 }}>
                <Typography fontWeight="bold" sx={{ fontSize:'0.95rem', mb: 0.3, lineHeight:1.3,
                  overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                  {p.title}
                </Typography>
                <Typography sx={{ color:'#666', fontSize:'0.8rem',
                  overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}
                  dangerouslySetInnerHTML={{ __html: p.description?.replace(/<[^>]+>/g,'').slice(0,120) || '' }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Album slide ─────────────────────────────────────────────────────────────

function AlbumSlide({ content }) {
  const {
    images = [],
    imageDuration = 5,
    displayMode = 'zone-cover',
    title = '',
    showTitle = false,
    bgColor = '#000',
  } = content || {};

  // Derive objectFit from displayMode
  const fit = (displayMode === 'zone-contain' || displayMode === 'fullscreen-contain') ? 'contain' : 'cover';

  const [idx, setIdx] = useState(0);
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    if (images.length <= 1 || displayMode === 'collage') return;
    const t = setInterval(() => {
      setEntering(true);
      setTimeout(() => {
        setIdx(i => (i + 1) % images.length);
        setEntering(false);
      }, 350);
    }, imageDuration * 1000);
    return () => clearInterval(t);
  }, [images, imageDuration, displayMode]);

  if (!images.length) return (
    <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', bgcolor:'#111' }}>
      <Typography sx={{ color:'#555' }}>Inga bilder i albumet</Typography>
    </Box>
  );

  // ── Collage mode: all images in responsive grid ───────────────────────────
  if (displayMode === 'collage') {
    const count = images.length;
    const cols = count === 1 ? 1 : count === 2 ? 2 : count <= 4 ? 2 : count <= 6 ? 3 : 4;
    return (
      <Box sx={{ position:'relative', width:'100%', height:'100%', bgcolor: bgColor, overflow:'hidden' }}>
        <Box sx={{ display:'grid', width:'100%', height:'100%',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridAutoRows: '1fr', gap: '2px' }}>
          {images.map((img, i) => (
            <Box key={i} sx={{ overflow:'hidden', position:'relative' }}>
              <img src={img} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            </Box>
          ))}
        </Box>
        {showTitle && title && (
          <Box sx={{ position:'absolute', bottom:0, left:0, right:0, px:4, py:2,
            background:'linear-gradient(to top, rgba(0,0,0,0.75), transparent)' }}>
            <Typography sx={{ color:'white', fontWeight:800, fontSize:'clamp(1.2rem,3vw,2.5rem)' }}>{title}</Typography>
          </Box>
        )}
      </Box>
    );
  }

  // ── Cycling mode ─────────────────────────────────────────────────────────
  return (
    <Box sx={{ position:'relative', width:'100%', height:'100%', bgcolor: bgColor, overflow:'hidden' }}>
      {images.map((img, i) => (
        <Box key={i} sx={{ position:'absolute', inset:0,
          opacity: i === idx ? (entering ? 0 : 1) : 0,
          transition: 'opacity 0.35s ease' }}>
          <img src={img} alt="" style={{ width:'100%', height:'100%', objectFit: fit }} />
        </Box>
      ))}

      {/* Title overlay */}
      {showTitle && title && (
        <Box sx={{ position:'absolute', bottom:0, left:0, right:0,
          px:'clamp(1.5rem,4vw,4rem)', py:'clamp(1rem,2.5vw,2rem)',
          background:'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)', zIndex:1 }}>
          <Typography sx={{ color:'white', fontWeight:800,
            textShadow:'0 2px 8px rgba(0,0,0,0.6)',
            fontSize:'clamp(1.2rem,3vw,2.5rem)', lineHeight:1.2 }}>{title}</Typography>
        </Box>
      )}

      {/* Progress dots */}
      {images.length > 1 && (
        <Box sx={{ position:'absolute', bottom: showTitle && title ? 'clamp(3.5rem,9vw,7rem)' : 14,
          left:0, right:0, display:'flex', justifyContent:'center', gap: 0.75, zIndex:2 }}>
          {images.map((_, i) => (
            <Box key={i} sx={{ width: 7, height: 7, borderRadius:'50%',
              bgcolor: i === idx ? 'white' : 'rgba(255,255,255,0.35)',
              transition:'background-color 0.3s', boxShadow:'0 1px 4px rgba(0,0,0,0.4)' }} />
          ))}
        </Box>
      )}
    </Box>
  );
}

// Returns true if this slide's displayMode wants to break out of zone layout
function isBreakoutSlide(slide) {
  const dm = slide?.content?.displayMode;
  return dm === 'fullscreen-contain' || dm === 'fullscreen-cover';
}

// ─── Main slide renderer ──────────────────────────────────────────────────────

function QRDisplaySlide({ content }) {
  const url = window.location.href;
  const {
    heading = 'Se bildspelet i din mobil',
    instruction = 'Skanna QR-koden med kameran',
    bgPresetId = '',
    bgColor = '#1a1a1a',
    textColor = '#ffffff',
    accentColor = '#FF6B00',
    logoUrl = '',
  } = content || {};

  const bg = bgPresetId
    ? (PRESET_BACKGROUNDS.find(b => b.id === bgPresetId)?.css || bgColor)
    : bgColor;

  return (
    <Box sx={{
      width:'100%', height:'100%',
      background: bg,
      display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      gap: 3, p: 4,
    }}>
      {heading && (
        <Typography sx={{
          color: textColor, fontWeight: 800,
          fontSize: 'clamp(1.4rem, 3vw, 2.8rem)',
          textAlign:'center', lineHeight: 1.2,
        }}>
          {heading}
        </Typography>
      )}

      <Box sx={{
        bgcolor: 'white', borderRadius: 3, p: 2.5,
        boxShadow: `0 0 60px ${accentColor}66`,
      }}>
        <QRCodeSVG
          value={url}
          size={Math.min(window.innerWidth, window.innerHeight) * 0.38}
          level="H"
          {...(logoUrl ? {
            imageSettings: {
              src: logoUrl,
              height: Math.min(window.innerWidth, window.innerHeight) * 0.07,
              width:  Math.min(window.innerWidth, window.innerHeight) * 0.07,
              excavate: true,
            }
          } : {})}
        />
      </Box>

      {instruction && (
        <Typography sx={{
          color: textColor, opacity: 0.75,
          fontSize: 'clamp(0.9rem, 1.8vw, 1.4rem)',
          textAlign:'center',
        }}>
          {instruction}
        </Typography>
      )}
    </Box>
  );
}

function BirthdaySlide({ content, companyId }) {
  const [celebrants, setCelebrants] = useState(null); // null = loading
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!companyId) { setCelebrants([]); return; }
    const q = query(collection(db, 'employees'), where('companyId', '==', companyId));
    const unsub = onSnapshot(q, snap => {
      const today = new Date();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayMMDD = `${mm}-${dd}`;
      const hits = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(e => {
          if (!e.birthDate) return false;
          const parts = e.birthDate.split('-'); // YYYY-MM-DD
          return `${parts[1]}-${parts[2]}` === todayMMDD;
        });
      setCelebrants(hits);
      setIdx(0);
    });
    return unsub;
  }, [companyId]);

  useEffect(() => {
    if (!celebrants || celebrants.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % celebrants.length), 6000);
    return () => clearInterval(t);
  }, [celebrants]);

  const {
    greeting = 'Grattis på födelsedagen!',
    bgColor = 'linear-gradient(135deg,#7b1fa2 0%,#e91e63 100%)',
    textColor = '#ffffff',
  } = content || {};

  // Still loading — transparent placeholder
  if (celebrants === null) return <Box sx={{ width:'100%', height:'100%', bgcolor:'#111' }} />;

  // Nobody's birthday today — return null so ZonePlayer skips this slide
  if (celebrants.length === 0) return null;

  const person = celebrants[idx];
  const photo  = person.photo || person.photoUrl || null;
  const name   = [person.firstName, person.lastName].filter(Boolean).join(' ') || person.name || 'En kollega';
  const age    = person.birthDate ? new Date().getFullYear() - Number(person.birthDate.split('-')[0]) : null;

  return (
    <Box sx={{
      width:'100%', height:'100%', background: bgColor, position:'relative', overflow:'hidden',
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap: 3,
    }}>
      {/* Confetti-like dots */}
      {[...Array(20)].map((_, i) => (
        <Box key={i} sx={{
          position:'absolute',
          top: `${Math.random()*100}%`, left: `${Math.random()*100}%`,
          width: `${8 + Math.random()*12}px`, height: `${8 + Math.random()*12}px`,
          borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          bgcolor: ['#FFD700','#FF6B00','#ffffff','#ff80ab','#b39ddb'][Math.floor(Math.random()*5)],
          opacity: 0.25 + Math.random() * 0.4,
          transform: `rotate(${Math.random()*360}deg)`,
        }} />
      ))}

      {/* Photo */}
      {photo ? (
        <Box sx={{
          width: 'clamp(160px,25vw,280px)', height: 'clamp(160px,25vw,280px)',
          borderRadius:'50%', overflow:'hidden', flexShrink:0,
          border:'6px solid rgba(255,255,255,0.6)',
          boxShadow:'0 8px 40px rgba(0,0,0,0.4)',
        }}>
          <img src={photo} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
        </Box>
      ) : (
        <Box sx={{
          width:'clamp(160px,25vw,280px)', height:'clamp(160px,25vw,280px)',
          borderRadius:'50%', bgcolor:'rgba(255,255,255,0.2)',
          display:'flex', alignItems:'center', justifyContent:'center',
          border:'6px solid rgba(255,255,255,0.4)',
          fontSize:'clamp(4rem,10vw,8rem)',
        }}>🎂</Box>
      )}

      {/* Name + age */}
      <Box sx={{ textAlign:'center', px: 4, zIndex: 1 }}>
        <Typography sx={{
          color: textColor, fontWeight: 900,
          fontSize: 'clamp(1.8rem,6vw,5rem)', lineHeight: 1.1, textShadow:'0 2px 12px rgba(0,0,0,0.3)',
        }}>{name}</Typography>
        {age && age > 0 && age < 120 && (
          <Typography sx={{
            color: textColor, opacity: 0.85,
            fontSize: 'clamp(1.2rem,3.5vw,3rem)', fontWeight: 700, mt: 0.5,
          }}>fyller {age} år idag! 🎉</Typography>
        )}
      </Box>

      {/* Greeting */}
      <Typography sx={{
        color: textColor, opacity: 0.9, textAlign:'center', px: 6,
        fontSize: 'clamp(1rem,2.5vw,2rem)', fontStyle:'italic',
        zIndex: 1,
      }}>"{greeting}"</Typography>

      {/* Multiple celebrants indicator */}
      {celebrants.length > 1 && (
        <Box sx={{ display:'flex', gap: 0.75, position:'absolute', bottom: 24 }}>
          {celebrants.map((_, i) => (
            <Box key={i} sx={{ width:10, height:10, borderRadius:'50%',
              bgcolor: i === idx ? 'white' : 'rgba(255,255,255,0.35)' }} />
          ))}
        </Box>
      )}
    </Box>
  );
}

function SlideRenderer({ slide, companyLogo, companyId, compact = false }) {
  if (!slide) return null;
  const { type, content } = slide;
  const wrapStyle = { position:'absolute', inset:0, overflow:'hidden', background:'#111' };
  switch (type) {
    case 'birthday': return <Box sx={wrapStyle}><BirthdaySlide content={content} companyId={companyId} /></Box>;
    case 'qr_display': return <Box sx={wrapStyle}><QRDisplaySlide content={{ ...content, logoUrl: content?.logoUrl || companyLogo || '' }} /></Box>;
    case 'text': return <Box sx={wrapStyle}><TextSlide content={content} compact={compact} /></Box>;
    case 'album': return <Box sx={wrapStyle}><AlbumSlide content={content} /></Box>;
    case 'media': return <Box sx={wrapStyle}><MediaSlide content={content} /></Box>;
    case 'image_url': return <Box sx={wrapStyle}><ImageUrlSlide content={content} /></Box>;
    case 'video': return <Box sx={wrapStyle}><VideoSlide content={content} /></Box>;
    case 'weather': return <Box sx={wrapStyle}><WeatherSlide content={content} compact={compact} /></Box>;
    case 'weather_map': return <Box sx={wrapStyle}><WeatherMapSlide content={content} /></Box>;
    case 'traffic': return <Box sx={wrapStyle}><TrafficSlide content={content} compact={compact} /></Box>;
    case 'rss': return <Box sx={wrapStyle}><RSSSlide content={content} compact={compact} /></Box>;
    case 'url':
    case 'calendar': return <Box sx={wrapStyle}><UrlSlide content={content} /></Box>;
    case 'world_clock': return <Box sx={wrapStyle}><WorldClockSlide content={content} /></Box>;
    case 'menu': return <Box sx={wrapStyle}><MenuSlide content={content} /></Box>;
    case 'lagbasn_reference': return <Box sx={wrapStyle}><LagbasnReferenceSlide content={content} /></Box>;
    case 'lagbasn_leads': return <Box sx={wrapStyle}><LagbasnLeadsSlide content={content} /></Box>;
    case 'lagbasn_wins': return <Box sx={wrapStyle}><LagbasnWinsSlide content={content} /></Box>;
    case 'date_tearoff': return <Box sx={wrapStyle}><DateTearoffSlide content={content} /></Box>;
    case 'emoji': return <Box sx={wrapStyle}><EmojiSlide content={content} /></Box>;
    case 'google_calendar':
    case 'ms_calendar':
    case 'power_bi':
    case 'sharepoint': return <Box sx={wrapStyle}><EmbedSlide content={content} /></Box>;
    case 'social_facebook': return <Box sx={wrapStyle}><FacebookSlide content={content} /></Box>;
    case 'social_instagram': return <Box sx={wrapStyle}><InstagramSlide content={content} /></Box>;
    case 'social_linkedin': return <Box sx={wrapStyle}><LinkedInSlide content={content} /></Box>;
    default:
      return (
        <Box sx={{ ...wrapStyle, display:'flex',alignItems:'center',justifyContent:'center' }}>
          <Typography sx={{ color:'#555' }}>Okänd slide-typ: {type}</Typography>
        </Box>
      );
  }
}

// ─── Swedish name day lookup (MM-DD) ────────────────────────────────────────
const SE_NAMEDAYS = {
  '01-01':'','01-02':'Svea','01-03':'Alfred, Alfrida','01-04':'Rut','01-05':'Hanna, Hannele',
  '01-06':'Kaspar, Melker, Baltasar','01-07':'August, Augusta','01-08':'Erland',
  '01-09':'Gunnar, Gunnhild','01-10':'Sigurd, Sigbritt','01-11':'Hilda, Hildur',
  '01-12':'Fridolf, Frideborg','01-13':'Knut','01-14':'Felix, Felicia',
  '01-15':'Laura, Lovis','01-16':'Hjalmar, Hjördis','01-17':'Anton, Antonia',
  '01-18':'Hasse, Håkan','01-19':'Henrik','01-20':'Fabian, Sebastian',
  '01-21':'Agnes, Agneta','01-22':'Vincent','01-23':'Frej, Freja','01-24':'Erika',
  '01-25':'Paul, Paula','01-26':'Bodil, Boel','01-27':'Göte, Göta','01-28':'Karl, Karla',
  '01-29':'Diana','01-30':'Gunilla, Gunhild','01-31':'Ivar, Joar',
  '02-01':'Max, Maximilian','02-02':'Kyndelsmässan','02-03':'Disa, Hjördis',
  '02-04':'Ansgar, Oscar','02-05':'Agata, Agda','02-06':'Dorotea, Dora',
  '02-07':'Rikard','02-08':'Berta, Bert','02-09':'Fanny, Franciska','02-10':'Iris',
  '02-11':'Yngve, Inge','02-12':'Evelina, Evelin','02-13':'Agne, Agnar','02-14':'Valentin',
  '02-15':'Sigfrid','02-16':'Julia, Julius','02-17':'Alexandra, Sandra',
  '02-18':'Frida, Fritiof','02-19':'Gabriella, Gabrielle','02-20':'Vidar, Vide',
  '02-21':'Hilding','02-22':'Pia','02-23':'Torsten, Torun','02-24':'Mattias, Mathias',
  '02-25':'Sigvard, Sivert','02-26':'Torgny, Torkel','02-27':'Lage','02-28':'Maria',
  '03-01':'Albin, Elvira','03-02':'Ernst, Erna','03-03':'Gunborg, Gunvor',
  '03-04':'Adrian, Adriana','03-05':'Tora, Tove','03-06':'Ebba, Ebbe','03-07':'Camilla',
  '03-08':'Siv, Saga','03-09':'Torborg, Torbjörn','03-10':'Edla, Edle',
  '03-11':'Edvard, Ed','03-12':'Viktoria','03-13':'Greger, Gregorius',
  '03-14':'Mathilda, Maud','03-15':'Kristoffer, Christoffer','03-16':'Herbert, Heribert',
  '03-17':'Gertrud, Gerd','03-18':'Edvard, Ed','03-19':'Josef, Josefin',
  '03-20':'Joakim, Jakim','03-21':'Bengt, Bengta','03-22':'Kennet, Cenneth',
  '03-23':'Gerda, Gerd','03-24':'Gabriel, Gabriella','03-25':'Marie Bebådelsedag',
  '03-26':'Emanuel','03-27':'Rudolf, Rut','03-28':'Malkolm, Malcolm',
  '03-29':'Jonas, Jonatan','03-30':'Holger, Holmfrid','03-31':'Ester, Estrid',
  '04-01':'Harald, Hervor','04-02':'Gudmund, Ingemund','04-03':'Ferdinand, Nanna',
  '04-04':'Marianne, Mariana','04-05':'Irene, Irja','04-06':'Vilhelm, Helmi',
  '04-07':'Procopius','04-08':'Silvia, Sylvia','04-09':'Otto, Ottilia',
  '04-10':'Ingvar, Ingvor','04-11':'Leo, Leona','04-12':'Liv',
  '04-13':'Artur, Artus','04-14':'Tiburtius, Valerian','04-15':'Olivia, Oliver',
  '04-16':'Patrik, Patricia','04-17':'Elias, Elia','04-18':'Valdemar, Volmar',
  '04-19':'Olaus, Ola','04-20':'Amalia, Amelie','04-21':'Ansgar, Anselm',
  '04-22':'Allan, Glenn','04-23':'Georg, Göran','04-24':'Vega',
  '04-25':'Markus, Mark','04-26':'Teresia, Terese','04-27':'Engelbrekt',
  '04-28':'Ture, Tyko','04-29':'Tyko, Ture','04-30':'Valborg',
  '05-01':'Valborg / Maj','05-02':'Filip, Filippa','05-03':'John, Jane',
  '05-04':'Monika, Mona','05-05':'Gotthard, Erhard','05-06':'Marit, Markus',
  '05-07':'Carina, Carita','05-08':'Åke','05-09':'Reidar, Reidun',
  '05-10':'Esbjörn, Styrbjörn','05-11':'Märta, Märit','05-12':'Charlotta, Lotta',
  '05-13':'Disa','05-14':'Halvard, Halvor','05-15':'Sofia, Sonja',
  '05-16':'Pernilla, Petronella','05-17':'Rebecka, Ruben','05-18':'Erik',
  '05-19':'Maj, Majken','05-20':'Karolina, Carola','05-21':'Konstantin, Conny',
  '05-22':'Hemming, Hemmig','05-23':'Desideria, Desirée','05-24':'Ivan, Vanja',
  '05-25':'Urban','05-26':'Vilhelmina, Helmi','05-27':'Beda, Blenda',
  '05-28':'Ingeborg, Inger','05-29':'Maximilian, Max','05-30':'Gunnar (alt)','05-31':'Petronella, Pernilla',
  '06-01':'Gun, Gunnel','06-02':'Rutger, Roger','06-03':'Ingemar, Gudmar',
  '06-04':'Solbritt, Solveig','06-05':'Sockendag / Ingemar','06-06':'Gustav, Gustaf (Nationaldag)',
  '06-07':'Robert, Robin','06-08':'Eivor, Majvor','06-09':'Börje, Birger',
  '06-10':'Svante, Boris','06-11':'Bertil, Berthold','06-12':'Eskil',
  '06-13':'Aina, Aino','06-14':'Basil, Balder','06-15':'Sigrid, Sigbritt',
  '06-16':'Axel, Axeline','06-17':'Torborg, Torbjörn','06-18':'Bjarne, Bjarni',
  '06-19':'Gervasius, Protasius','06-20':'Linda','06-21':'Alf, Alvar',
  '06-22':'Paulina, Paula','06-23':'Adolf, Adolph','06-24':'Johannes (Midsommar)',
  '06-25':'David, Salomon','06-26':'Rakel, Lea','06-27':'Selma, Anselm',
  '06-28':'Leo, Leon','06-29':'Peter, Petra','06-30':'Elof, Leif',
  '07-01':'Aron, Mirjam','07-02':'Rosa, Rosita','07-03':'Aurora, Gry',
  '07-04':'Ulrika, Ulla','07-05':'Laila, Ritva','07-06':'Esaias, Jessika',
  '07-07':'Klas, Nicolaus','07-08':'Kjerstin, Kerstin','07-09':'Erland (alt)','07-10':'Sven',
  '07-11':'Eleonora, Ellinor','07-12':'Herman, Hermine','07-13':'Ulrika (alt)','07-14':'Joel, Judit',
  '07-15':'Ragnhild, Ragnvald','07-16':'Reinhold, Reine','07-17':'Bruno, Brunolf',
  '07-18':'Fredrik, Fredrika','07-19':'Sara, Sarai','07-20':'Margareta, Greta',
  '07-21':'Johanna, Hanna','07-22':'Maria Magdalena','07-23':'Emma, Emmeli',
  '07-24':'Kristina, Kia','07-25':'Jakob, Jack','07-26':'Anna, Johanna',
  '07-27':'Marta, Märta','07-28':'Botvid, Seved','07-29':'Olof, Olivia',
  '07-30':'Algot, Algor','07-31':'Helena, Elen',
  '08-01':'Per, Pernilla','08-02':'Karin, Karna','08-03':'Tage','08-04':'Arne, Arnold',
  '08-05':'Ulrik, Alrik','08-06':'Alf, Alvar (alt)','08-07':'Dennis, Denise',
  '08-08':'Silvia, Sylvia (alt)','08-09':'Roland','08-10':'Lars, Lorentz',
  '08-11':'Susanna, Susanne','08-12':'Klara','08-13':'Kaj, Majken',
  '08-14':'Uno, Una','08-15':'Maria / Marias himmelsfärd','08-16':'Brynolf, Brynhild',
  '08-17':'Verner, Veronika','08-18':'Ellen, Elin','08-19':'Magnus, Mans',
  '08-20':'Bernhard, Bernt','08-21':'Jon, Jonna','08-22':'Henrietta, Henrika',
  '08-23':'Signe, Signhild','08-24':'Bartolomeus','08-25':'Lovisa, Ludvig',
  '08-26':'Östen, Östin','08-27':'Rolf, Raoul','08-28':'Augustinus',
  '08-29':'Hans, Hampus','08-30':'Albert, Albertina','08-31':'Arvid, Vidar',
  '09-01':'Samuel, Sam','09-02':'Justus, Justina','09-03':'Alfhild, Alvida',
  '09-04':'Gisela, Gizela','09-05':'Adela, Heidi','09-06':'Rune',
  '09-07':'Regina, Raine','09-08':'Mariadag','09-09':'Anita, Annita',
  '09-10':'Tord, Turid','09-11':'Dagny, Helny','09-12':'Åse, Åsa',
  '09-13':'Sture','09-14':'Heliga kors','09-15':'Sigrun, Sigrid (alt)',
  '09-16':'Cornelia, Cornelius','09-17':'Hildegard, Hildigerd','09-18':'Orvar, Arvid (alt)',
  '09-19':'Fredrika, Frida','09-20':'Elise, Lisa','09-21':'Matteus, Mattias',
  '09-22':'Maurits, Morris','09-23':'Tekla, Teofil','09-24':'Gerhard, Gerd',
  '09-25':'Tryggve, Trygg','09-26':'Enar, Einer','09-27':'Dagmar, Rigmor',
  '09-28':'Lennart, Leonard','09-29':'Mikael, Mikaela','09-30':'Ragnvald, Ragnar',
  '10-01':'Ragnar','10-02':'Ludvig, Lovis','10-03':'Evald, Osvald',
  '10-04':'Frans, Francisca','10-05':'Britt, Birgit','10-06':'Bruno, Brunolf',
  '10-07':'Birgitta, Britta','10-08':'Nils (alt), Nisse','10-09':'Ingrid, Inga',
  '10-10':'Harry, Harald (alt)','10-11':'Björn, Bjärnhild','10-12':'Valfrid, Manfred',
  '10-13':'Stellan','10-14':'Sten, Stina','10-15':'Hedvig, Hedda',
  '10-16':'Finn, Finna','10-17':'Antonia, Anton (alt)','10-18':'Lukas, Lukia',
  '10-19':'Tore, Tor','10-20':'Sibylla, Sybil','10-21':'Ursula, Yrsa',
  '10-22':'Malin, Magdalena','10-23':'Severin, Örjan','10-24':'Evert, Ewert',
  '10-25':'Inga, Ingrid','10-26':'Amanda, Amandus','10-27':'Sabina',
  '10-28':'Simon, Simone','10-29':'Viola','10-30':'Edith, Edit',
  '10-31':'Halloween / Edit (alt)',
  '11-01':'Alla helgons dag','11-02':'Alla själars dag','11-03':'Hubert, Hubertus',
  '11-04':'Sverker, Charles','11-05':'Eugen, Eugenia','11-06':'Gustav Adolf',
  '11-07':'Ingegerd, Ingela','11-08':'Vendela, Vendelin','11-09':'Teodor, Theodor',
  '11-10':'Martin Luther','11-11':'Mårten, Martin','11-12':'Konrad, Kurt',
  '11-13':'Kristian, Christian','11-14':'Emil, Emilia','11-15':'Leopold',
  '11-16':'Vibeke, Edmund','11-17':'Naemi, Helga','11-18':'Lillemor, Mia',
  '11-19':'Elisabet, Lisbet','11-20':'Pontus, Bengt (alt)','11-21':'Helga, Olga',
  '11-22':'Cecilia, Cilla','11-23':'Klemens, Klement','11-24':'Gudrun, Ragnhild',
  '11-25':'Katarina, Katharina','11-26':'Linus','11-27':'Astrid, Sassa',
  '11-28':'Malena, Magdalena (alt)','11-29':'Sune','11-30':'Andreas, Anders',
  '12-01':'Oskar, Ossian','12-02':'Beata, Beatrice','12-03':'Frans Xaver, Frida',
  '12-04':'Barbara, Barbro','12-05':'Sven, Svante','12-06':'Nikolaus',
  '12-07':'Angela, Angelika','12-08':'Virginia, Virgo','12-09':'Anna, Täcka',
  '12-10':'Malin, Malena','12-11':'Daniel, Daniela','12-12':'Alexander, Sandra',
  '12-13':'Lucia','12-14':'Sten (alt), Gillis','12-15':'Gottfrid, Godfrey',
  '12-16':'Assar, Ansgarius','12-17':'Stig','12-18':'Abraham, Isak',
  '12-19':'Simon, Simone','12-20':'Israel, Gottfrid (alt)','12-21':'Tomas, Viola (alt)',
  '12-22':'Steinunn, Inga (alt)','12-23':'Adam, Eva (fördag)','12-24':'Adam, Eva (Julafton)',
  '12-25':'Juldagen','12-26':'Stefan, Staffan','12-27':'Johannes, Johan',
  '12-28':'Oskyldiga barns dag','12-29':'Jonathan, Jonatan','12-30':'Abel, Set','12-31':'Sylvester',
};

function getSweNameDay(date) {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return SE_NAMEDAYS[`${mm}-${dd}`] || '';
}

// ─── Lingon border SVG ────────────────────────────────────────────────────────

function LingonBorder() {
  const W = 1920, H = 1080, B = 90; // virtual canvas size and border thickness

  // Generate berry + leaf motifs along all 4 sides
  const items = [];
  const spacing = 72;

  // Helper to push alternating berry/leaf
  const push = (x, y, rot) => {
    const n = items.length;
    items.push({ x, y, rot, type: n % 3 === 2 ? 'leaf' : 'berry' });
  };

  for (let x = spacing / 2; x <= W; x += spacing) push(x, B / 2, 0);           // top
  for (let x = spacing / 2; x <= W; x += spacing) push(x, H - B / 2, 180);       // bottom
  for (let y = B + spacing / 2; y <= H - B; y += spacing) push(B / 2, y, -90);   // left
  for (let y = B + spacing / 2; y <= H - B; y += spacing) push(W - B / 2, y, 90); // right

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }}>
      {/* Dark teal border strips */}
      <rect x={0}     y={0}     width={W}   height={B}   fill="#1d6448" />
      <rect x={0}     y={H-B}   width={W}   height={B}   fill="#1d6448" />
      <rect x={0}     y={B}     width={B}   height={H-B*2} fill="#1d6448" />
      <rect x={W-B}   y={B}     width={B}   height={H-B*2} fill="#1d6448" />

      {/* Motifs */}
      {items.map((item, i) => (
        <g key={i} transform={`translate(${item.x},${item.y}) rotate(${item.rot})`}>
          {item.type === 'berry' ? (
            <>
              {/* Berry */}
              <circle cx={0} cy={0} r={18} fill="#c0221a" />
              <circle cx={0} cy={0} r={11} fill="#a01515" opacity={0.5} />
              <circle cx={-4} cy={-6} r={4} fill="rgba(255,255,255,0.25)" />
              {/* Calyx dots */}
              <circle cx={0} cy={-19} r={3} fill="#145236" />
              <line x1={-5} y1={-17} x2={-10} y2={-26} stroke="#145236" strokeWidth={2.5} strokeLinecap="round" />
              <line x1={5}  y1={-17} x2={10}  y2={-26} stroke="#145236" strokeWidth={2.5} strokeLinecap="round" />
            </>
          ) : (
            <>
              {/* Leaf */}
              <ellipse cx={0} cy={0} rx={8} ry={22} fill="#2a7a54" />
              <line x1={0} y1={-20} x2={0} y2={20} stroke="#1d5e3e" strokeWidth={1.5} />
              <line x1={0} y1={-8} x2={-6} y2={-14} stroke="#1d5e3e" strokeWidth={1} />
              <line x1={0} y1={0}  x2={-6} y2={-6}  stroke="#1d5e3e" strokeWidth={1} />
              <line x1={0} y1={8}  x2={-6} y2={2}   stroke="#1d5e3e" strokeWidth={1} />
            </>
          )}
        </g>
      ))}
    </svg>
  );
}

function DateTearoffSlide({ content }) {
  const { style = 'classic', lang = 'sv', subtitle } = content || {};
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const locale = lang === 'en' ? 'en-GB' : 'sv-SE';
  const dayNum   = now.getDate();
  const monthStr = now.toLocaleDateString(locale, { month: 'long' });
  const yearStr  = now.getFullYear();
  const weekdayStr = subtitle || now.toLocaleDateString(locale, { weekday: 'long' });

  // Veckonummer
  const weekNum = (() => {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const year = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - year) / 86400000) + 1) / 7);
  })();

  const themes = {
    classic: {
      bg: '#ffffff',
      headerBg: '#cc2200',
      headerColor: '#ffffff',
      dayColor: '#1a1a1a',
      monthColor: '#cc2200',
      footerColor: '#888',
      fontFamily: '"Georgia", serif',
      spiralColor: '#999',
    },
    modern: {
      bg: '#1a1a1a',
      headerBg: '#FF6B00',
      headerColor: '#ffffff',
      dayColor: '#ffffff',
      monthColor: '#FF6B00',
      footerColor: '#666',
      fontFamily: '"Arial", sans-serif',
      spiralColor: '#444',
    },
    vintage: {
      bg: '#f5e6c8',
      headerBg: '#8B4513',
      headerColor: '#f5e6c8',
      dayColor: '#3d2009',
      monthColor: '#8B4513',
      footerColor: '#a07040',
      fontFamily: '"Palatino Linotype", "Book Antiqua", serif',
      spiralColor: '#c0a080',
    },
  };

  // ── Lingon style ──────────────────────────────────────────────────────────
  if (style === 'lingon') {
    const nameDay = getSweNameDay(now);
    const dayCapitalized = weekdayStr.charAt(0).toUpperCase() + weekdayStr.slice(1);
    return (
      <Box sx={{ width:'100%', height:'100%', bgcolor:'#eeece8', position:'relative', overflow:'hidden',
        display:'flex', alignItems:'center', justifyContent:'center' }}>
        <LingonBorder />
        {/* Calendar paper */}
        <Box sx={{ position:'relative', zIndex:1, bgcolor:'white', borderRadius:'4px',
          width:'54%', boxShadow:'0 12px 60px rgba(0,0,0,0.30), 0 2px 10px rgba(0,0,0,0.15)',
          display:'flex', flexDirection:'column', overflow:'hidden' }}>

          {/* Grey binding strip */}
          <Box sx={{ bgcolor:'#c8c8c8', height:44, display:'flex', alignItems:'center',
            justifyContent:'center', flexShrink:0 }}>
            <Box sx={{ width:60, height:26, bgcolor:'#b0b0b0', borderRadius:'3px',
              boxShadow:'inset 0 2px 4px rgba(0,0,0,0.2)' }} />
          </Box>

          {/* Month */}
          <Box sx={{ textAlign:'center', pt: 3, pb: 0.5 }}>
            <Typography sx={{ fontSize:'2.8rem', fontWeight:900, letterSpacing:4,
              fontFamily:'"Arial Black", "Arial", sans-serif', color:'#111' }}>
              {monthStr.toUpperCase()}
            </Typography>
          </Box>

          {/* Day number */}
          <Box sx={{ textAlign:'center', lineHeight:1, px:2 }}>
            <Typography sx={{ fontSize:'13rem', fontWeight:900, lineHeight:0.9,
              fontFamily:'"Arial Black", "Arial", sans-serif', color:'#111' }}>
              {dayNum}
            </Typography>
          </Box>

          {/* Weekday + name day */}
          <Box sx={{ textAlign:'center', pt:1, pb:1 }}>
            <Typography sx={{ fontSize:'2.8rem', fontWeight:900,
              fontFamily:'"Arial", sans-serif', color:'#111' }}>
              {dayCapitalized}
            </Typography>
            {nameDay && (
              <Typography sx={{ fontSize:'1.5rem', color:'#444',
                fontFamily:'"Arial", sans-serif', mt:0.5 }}>
                {nameDay}
              </Typography>
            )}
          </Box>

          {/* Year + week */}
          <Box sx={{ textAlign:'center', pt:1.5, pb:3, borderTop:'1px solid #e0e0e0', mx:4, mt:1 }}>
            <Typography sx={{ fontSize:'1.3rem', fontWeight:700, color:'#222',
              fontFamily:'"Arial", sans-serif', letterSpacing:1 }}>
              {yearStr} vecka {weekNum}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const t = themes[style] || themes.classic;

  return (
    <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%',
      bgcolor: style === 'modern' ? '#0f172a' : '#e8e0d8' }}>
      {/* Calendar block */}
      <Box sx={{
        width: 420, borderRadius: 3,
        boxShadow: '0 20px 60px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)',
        overflow: 'hidden', position: 'relative',
        fontFamily: t.fontFamily,
      }}>
        {/* Spiral holes at top */}
        <Box sx={{ position:'absolute', top: -2, left: 0, right: 0, zIndex: 10,
          display:'flex', justifyContent:'space-around', px: 3 }}>
          {Array.from({length: 7}).map((_,i) => (
            <Box key={i} sx={{ width: 18, height: 18, borderRadius:'50%',
              bgcolor: t.spiralColor, border:'3px solid rgba(0,0,0,0.2)',
              boxShadow:'inset 0 1px 3px rgba(0,0,0,0.4)' }} />
          ))}
        </Box>

        {/* Header strip (month + year) */}
        <Box sx={{ bgcolor: t.headerBg, pt: 3, pb: 1.5, px: 3, textAlign:'center' }}>
          <Typography sx={{ color: t.headerColor, fontSize:'1.6rem', fontWeight: 700,
            textTransform:'uppercase', letterSpacing: 4, fontFamily: t.fontFamily }}>
            {monthStr}
          </Typography>
          <Typography sx={{ color: t.headerColor, fontSize:'0.9rem', opacity: 0.8,
            letterSpacing: 2, fontFamily: t.fontFamily }}>
            {yearStr}
          </Typography>
        </Box>

        {/* Torn edge effect */}
        <Box sx={{
          height: 12, bgcolor: t.headerBg,
          clipPath: 'polygon(0% 0%, 3% 100%, 7% 30%, 12% 90%, 17% 10%, 22% 80%, 27% 20%, 32% 70%, 37% 5%, 42% 85%, 47% 15%, 52% 75%, 57% 25%, 62% 65%, 67% 10%, 72% 80%, 77% 20%, 82% 60%, 87% 5%, 92% 75%, 97% 15%, 100% 50%, 100% 0%)',
        }} />

        {/* Main date number */}
        <Box sx={{ bgcolor: t.bg, textAlign:'center', py: 3, px: 4 }}>
          <Typography sx={{
            fontSize: '10rem', fontWeight: 900, lineHeight: 1,
            color: t.dayColor, fontFamily: t.fontFamily,
            textShadow: style === 'classic' ? '2px 4px 8px rgba(0,0,0,0.12)' : 'none',
          }}>
            {dayNum}
          </Typography>
        </Box>

        {/* Bottom strip (weekday) */}
        <Box sx={{ bgcolor: t.bg, borderTop:`2px solid ${style==='vintage'?'#c0a080':'#e0e0e0'}`,
          pt: 1, pb: 2, textAlign:'center' }}>
          <Typography sx={{ color: t.monthColor, fontSize:'1.3rem', fontWeight: 600,
            textTransform:'uppercase', letterSpacing: 3, fontFamily: t.fontFamily }}>
            {weekdayStr}
          </Typography>
          <Typography sx={{ color: t.footerColor, fontSize:'0.8rem', mt: 0.5, letterSpacing: 2,
            fontFamily: t.fontFamily }}>
            {lang === 'sv' ? `Vecka ${weekNum}` : `Week ${weekNum}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export { SlideRenderer };

// ─── Zone player ─────────────────────────────────────────────────────────────

// Separate progress bar so it re-renders independently (avoids parent flickering)
function ZoneProgressBar({ duration }) {
  const [elapsed, setElapsed] = useState(0);
  const ref = useRef(0);
  useEffect(() => {
    ref.current = 0; setElapsed(0);
    const t = setInterval(() => { ref.current += 100; setElapsed(ref.current); }, 100);
    return () => clearInterval(t);
  }, [duration]);
  const pct = Math.min((elapsed / duration) * 100, 100);
  return (
    <Box sx={{ position:'absolute', bottom:0, left:0, right:0, height:2, bgcolor:'rgba(255,255,255,0.1)' }}>
      <Box sx={{ height:'100%', bgcolor:'rgba(255,107,0,0.6)', width:`${pct}%`, transition:'width 0.1s linear' }} />
    </Box>
  );
}

function ZonePlayer({ slides, zone, companyLogo, companyId, onCurrentSlide }) {
  const isCompact = zone.startsWith('SIDFOT');

  // Stable reference: only changes when slides array or zone changes (not every 100ms parent render)
  const zoneSlides = useMemo(
    () => slides.filter(s => {
      const z = s.zone || 'HUVUDANSLAG';
      if (z === zone) return true;
      // Legacy: plain 'SIDFOT' (no number) maps to the first sidfot column
      if (zone === 'SIDFOT-1' && z === 'SIDFOT') return true;
      return false;
    }),
    [slides, zone]
  );

  const [idx, setIdx] = useState(0);
  const idxRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!zoneSlides.length) return;
    if (timerRef.current) clearInterval(timerRef.current);
    // If current idx is out of bounds after slide list changes, reset
    if (idxRef.current >= zoneSlides.length) { idxRef.current = 0; setIdx(0); }
    const tick = () => {
      const cur = zoneSlides[idxRef.current];
      const dur = (cur?.duration || 10) * 1000;
      timerRef.current = setTimeout(() => {
        let next = idxRef.current;
        for (let i = 1; i <= zoneSlides.length; i++) {
          const candidate = (idxRef.current + i) % zoneSlides.length;
          if (isSlideScheduleActive(zoneSlides[candidate])) { next = candidate; break; }
        }
        idxRef.current = next;
        setIdx(next);
      }, dur);
    };
    tick();
    return () => clearTimeout(timerRef.current);
  }, [zoneSlides, idx]); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = zoneSlides[idx] || zoneSlides[0];
  const duration = (cur?.duration || 10) * 1000;

  // Report current slide to parent (for breakout detection) — must be before any early return
  useEffect(() => { onCurrentSlide?.(cur); }, [cur]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!zoneSlides.length) return (
    <Box sx={{ width:'100%', height:'100%', bgcolor:'#0d0d0d', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Typography sx={{ color:'#333', fontSize:'0.75rem' }}>{zone}</Typography>
    </Box>
  );

  return (
    <Box sx={{ width:'100%', height:'100%', position:'relative', overflow:'hidden' }}>
      <SlideRenderer slide={cur} companyLogo={companyLogo} companyId={companyId || ''} compact={isCompact} />
      <ZoneProgressBar duration={duration} />
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function InstallBanner() {
  const storageKey = 'castn-install-banner-dismissed';
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only show on mobile, not if already installed as PWA, not if dismissed before
    if (isMobile && !isStandalone && !sessionStorage.getItem(storageKey)) {
      const t = setTimeout(() => setOpen(true), 3000);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function dismiss() {
    setOpen(false);
    sessionStorage.setItem(storageKey, '1');
  }

  const instruction = isIOS
    ? 'Tryck på dela-knappen (□↑) i Safari och välj "Lägg till på hemskärmen"'
    : 'Tryck på menyn (⋮) i Chrome och välj "Lägg till på startskärmen"';

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 16 }, maxWidth: 420, width: '90vw' }}
    >
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white', borderRadius: 2, p: 2,
        boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
        border: '1px solid #FF6B00',
        display: 'flex', flexDirection: 'column', gap: 1.5, position: 'relative',
      }}>
        <MuiIconButton size="small" onClick={dismiss}
          sx={{ position:'absolute', top: 6, right: 6, color:'#aaa' }}>
          <CloseIcon fontSize="small" />
        </MuiIconButton>
        <Box sx={{ display:'flex', alignItems:'center', gap: 1.5, pr: 3 }}>
          <Typography fontSize="1.8rem">📲</Typography>
          <Box>
            <Typography fontWeight="bold" fontSize="0.95rem">Installera som app</Typography>
            <Typography variant="caption" sx={{ color:'#aaa' }}>
              Öppna bildspelet direkt från hemskärmen – utan webbläsare
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color:'#ccc', lineHeight: 1.5 }}>
          {instruction}
        </Typography>
        <MuiButton size="small" onClick={dismiss}
          sx={{ alignSelf:'flex-end', color:'#FF6B00', fontWeight:'bold' }}>
          Förstått
        </MuiButton>
      </Box>
    </Snackbar>
  );
}

export default function CastNDisplay() {
  const { token } = useParams();
  const [display, setDisplay] = useState(null);
  const [slides, setSlides] = useState([]);
  const [castTheme, setCastTheme] = useState(null);
  const [castLayout, setCastLayout] = useState({});   // layout settings from cast doc
  const [currentIdx, setCurrentIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [castBgUrl, setCastBgUrl] = useState(null);
  const [huvedSlide, setHuvudSlide] = useState(null); // tracks what HUVUD zone is currently showing
  const [loading, setLoading] = useState(true);

  const THEMES = {
    tikab:  { footerBg: '#1a1a1a', accent: '#FF6B00' },
    nordic: { footerBg: '#0d1b2a', accent: '#2196F3' },
    dark:   { footerBg: '#0d0d0d', accent: '#aaaaaa' },
    light:  { footerBg: '#f5f5f5', accent: '#333333' },
    forest: { footerBg: '#0a1f15', accent: '#4caf50' },
  };
  const elapsedRef = useRef(0);
  const timerRef = useRef(null);

  // Fetch display by token
  useEffect(() => {
    if (!token) return;
    const q = query(collection(db, 'displays'), where('token', '==', token.toUpperCase()));
    const unsub = onSnapshot(q, snap => {
      if (!snap.empty) {
        const d = snap.docs[0];
        setDisplay({ id: d.id, ...d.data() });
      }
      setLoading(false);
    });
    return unsub;
  }, [token]);

  // Heartbeat: update lastSeen every 60 s so the management page can show online status
  useEffect(() => {
    if (!display?.id) return;
    const ping = () => updateDoc(doc(db, 'displays', display.id), { lastSeen: serverTimestamp() }).catch(() => {});
    ping(); // immediate
    const t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, [display?.id]);

  // Fetch company logo
  useEffect(() => {
    if (!display?.companyId) return;
    getDoc(doc(db, 'companies', display.companyId)).then(snap => {
      if (snap.exists()) setCompanyLogo(snap.data().logo || null);
    });
  }, [display?.companyId]);

  // Fetch cast + slides
  useEffect(() => {
    if (!display?.castId) { setSlides([]); return; }
    const unsub = onSnapshot(doc(db, 'casts', display.castId), async snap => {
      if (!snap.exists()) return;
      const castData = { id: snap.id, ...snap.data() };
      setCastTheme(castData.theme || 'tikab');
      setCastBgUrl(castData.backgroundImageUrl || null);
      setCastLayout({
        displayLayout:   castData.displayLayout,
        panelStyle:      castData.panelStyle,
        sidfotColumns:   castData.sidfotColumns,
        sidoRows:        castData.sidoRows,
        panelGap:        castData.panelGap,
        panelRadius:     castData.panelRadius,
      });
      const slideRefs = castData.slides || [];
      if (slideRefs.length === 0) { setSlides([]); return; }
      // Fetch all slides
      const slidePromises = slideRefs.map(sr =>
        getDoc(doc(db, 'slides', sr.slideId)).then(s => s.exists() ? { id: s.id, ...s.data(), duration: sr.duration, zone: sr.zone } : null)
      );
      const loaded = (await Promise.all(slidePromises)).filter(Boolean).filter(s => s.active !== false).filter(isSlideScheduleActive);
      setSlides(loaded);
    });
    return unsub;
  }, [display?.castId]);

  // Slide timer
  useEffect(() => {
    if (!slides.length) return;
    if (timerRef.current) clearInterval(timerRef.current);
    elapsedRef.current = 0;
    setElapsed(0);
    const currentDuration = (slides[currentIdx]?.duration || 10) * 1000;
    timerRef.current = setInterval(() => {
      elapsedRef.current += 100;
      setElapsed(elapsedRef.current);
      if (elapsedRef.current >= currentDuration) {
        elapsedRef.current = 0;
        setElapsed(0);
        setCurrentIdx(prev => {
          // Find next schedule-active slide (skip inactive ones)
          for (let i = 1; i <= slides.length; i++) {
            const next = (prev + i) % slides.length;
            if (isSlideScheduleActive(slides[next])) return next;
          }
          return (prev + 1) % slides.length; // fallback
        });
      }
    }, 100);
    return () => clearInterval(timerRef.current);
  }, [slides, currentIdx]);

  // ─── No cast: show token ───────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ width:'100vw', height:'100vh', bgcolor:'#111', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <CircularProgress sx={{ color:'#FF6B00' }} />
      </Box>
    );
  }

  if (!display || !display.castId) {
    return (
      <Box sx={{ width:'100vw', height:'100vh', bgcolor:'#0d0d0d', display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center', gap: 4 }}>
        <Typography sx={{ color:'#444', fontSize:'1rem', letterSpacing: 4, textTransform:'uppercase' }}>CastN Digital Signage</Typography>
        <Box sx={{ border:'3px solid #FF6B00', borderRadius: 3, px: 6, py: 4, textAlign:'center' }}>
          <Typography sx={{ color:'#555', fontSize:'1rem', mb: 1 }}>Skärmkod</Typography>
          <Typography sx={{ color:'#fff', fontSize:'5rem', fontWeight:'bold', letterSpacing: 12, fontFamily:'monospace' }}>
            {token?.toUpperCase()}
          </Typography>
        </Box>
        <Typography sx={{ color:'#444', fontSize:'0.9rem' }}>
          Gå till {window.location.origin}/castn/connect och ange koden ovan
        </Typography>
      </Box>
    );
  }

  // ─── Cast playing ──────────────────────────────────────────────────────────
  // Cast settings take priority over display settings
  const displayLayout   = castLayout.displayLayout   || display.displayLayout   || 'fullscreen';
  const panelStyle      = castLayout.panelStyle      || display.panelStyle      || 'classic';
  const sidfotColumns   = castLayout.sidfotColumns   || display.sidfotColumns   || 1;
  const sidoRows        = castLayout.sidoRows        || display.sidoRows        || 1;
  const bgImage = castBgUrl;

  const currentSlide = slides[currentIdx] || null;
  const duration = (currentSlide?.duration || 10) * 1000;
  const progress = Math.min((elapsed / duration) * 100, 100);

  const theme = THEMES[castTheme || 'tikab'] || THEMES.tikab;

  // Glass panel style helpers
  const gap  = panelStyle === 'glass' ? (castLayout.panelGap    ?? 8)  : 0;
  const rad  = panelStyle === 'glass' ? `${castLayout.panelRadius ?? 12}px` : 0;

  // Footer bar (shared across layouts)
  const FooterBar = () => (
    <Box sx={{ height: 56, position:'relative', zIndex: 3, flexShrink: 0,
      bgcolor: bgImage && panelStyle === 'glass' ? 'rgba(0,0,0,0.55)' : theme.footerBg,
      backdropFilter: bgImage && panelStyle === 'glass' ? 'blur(8px)' : 'none',
      display:'flex', alignItems:'center', justifyContent:'space-between', px: 3 }}>
      <Clock />
      {companyLogo && (
        <img src={companyLogo} alt="Logo" style={{ height: 36, objectFit:'contain', maxWidth: 160 }} />
      )}
      <Box sx={{ position:'absolute', bottom: 0, left: 0, right: 0, height: 3, bgcolor:'rgba(255,255,255,0.1)' }}>
        <Box sx={{ height:'100%', bgcolor: theme.accent, width:`${progress}%`, transition:'width 0.1s linear' }} />
      </Box>
    </Box>
  );
  const panelOpacity = castLayout.panelOpacity ?? 0.55;
  const panelBlur    = castLayout.panelBlur    ?? 6;
  const panelColor   = castLayout.panelColor   ?? '#000000';
  const hexToRgb = (hex) => {
    const h = hex.replace('#','');
    return `${parseInt(h.slice(0,2),16)},${parseInt(h.slice(2,4),16)},${parseInt(h.slice(4,6),16)}`;
  };
  // classic = transparent panels (content has own bg); glass = colored panel overlay with blur
  const paneBg       = panelStyle === 'glass' ? `rgba(${hexToRgb(panelColor)},${panelOpacity})` : 'transparent';
  const paneBackdrop = panelStyle === 'glass' ? `blur(${panelBlur}px)` : 'none';

  // Breakout: when HUVUD slide wants to fill the whole screen, hide side/footer panels
  const breakout = isBreakoutSlide(huvedSlide);

  // Sidfot zone names for N columns.
  const SIDFOT_ZONES = ['SIDFOT-1','SIDFOT-2','SIDFOT-3','SIDFOT-4'];

  // Sidofältet: 1–3 equal rows
  function SidoPanel() {
    if (sidoRows === 1) return (
      <Box sx={{ width:'100%', height:'100%', position:'relative',
        borderRadius: rad, overflow:'hidden', bgcolor: paneBg, backdropFilter: paneBackdrop }}>
        <ZonePlayer slides={slides} zone="SIDOFÄLTET" companyLogo={companyLogo} companyId={display?.companyId} />
      </Box>
    );
    const zones = sidoRows === 2 ? ['SIDO-Ö','SIDO-N'] : ['SIDO-Ö','SIDO-N','SIDO-3'];
    return (
      <Box sx={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', gap: `${gap}px` }}>
        {zones.map((zone, i) => (
          <Box key={i} sx={{ flex:1, position:'relative', borderRadius: rad, overflow:'hidden',
            bgcolor: paneBg, backdropFilter: paneBackdrop }}>
            <ZonePlayer slides={slides} zone={zone} companyLogo={companyLogo} companyId={display?.companyId} />
          </Box>
        ))}
      </Box>
    );
  }

  // Sidfot row: N equal columns
  function SidfotRow({ height = 180 }) {
    return (
      <Box sx={{ height, display:'flex', gap: `${gap}px` }}>
        {Array.from({ length: sidfotColumns }, (_, i) => (
          <Box key={i} sx={{ flex:1, position:'relative', borderRadius: rad, overflow:'hidden',
            bgcolor: paneBg, backdropFilter: paneBackdrop }}>
            <ZonePlayer slides={slides} zone={SIDFOT_ZONES[i]} companyLogo={companyLogo} companyId={display?.companyId} />
          </Box>
        ))}
      </Box>
    );
  }

  // Two-zone layout: HUVUD 70% left, SIDO 30% right
  if (displayLayout === 'two-zone') {
    return (
      <Box sx={{ width:'100vw', height:'100vh', bgcolor: bgImage ? '#000' : '#111',
        display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>
        {bgImage && (
          <>
            <img src={bgImage} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', zIndex:0 }} />
            <Box sx={{ position:'absolute', inset:0, bgcolor:'rgba(0,0,0,0.2)', zIndex:1 }} />
          </>
        )}
        <Box sx={{ flex:1, display:'flex', overflow:'hidden', position:'relative', zIndex:2,
          gap: breakout ? 0 : `${gap}px`, p: breakout ? 0 : `${gap}px`, pb: 0,
          transition:'padding 0.4s ease, gap 0.4s ease' }}>
          <Box sx={{ flex: breakout ? 10 : 7, position:'relative',
            borderRadius: breakout ? 0 : rad, overflow:'hidden',
            bgcolor: paneBg, backdropFilter: paneBackdrop,
            transition:'flex 0.4s ease, border-radius 0.4s ease',
            ...(panelStyle === 'classic' && !breakout && { borderRight:'2px solid #1a1a1a' }) }}>
            <ZonePlayer slides={slides} zone="HUVUDANSLAG" companyLogo={companyLogo} companyId={display?.companyId} onCurrentSlide={setHuvudSlide} />
          </Box>
          <Box sx={{ flex: breakout ? 0 : 3, position:'relative', overflow:'hidden', minWidth:0,
            transition:'flex 0.4s ease' }}>
            <SidoPanel />
          </Box>
        </Box>
        {!breakout && <FooterBar />}
      </Box>
    );
  }

  // Three-zone layout: HUVUD + SIDFOT (left 70%), SIDO (right 30%)
  if (displayLayout === 'three-zone') {
    return (
      <Box sx={{ width:'100vw', height:'100vh', bgcolor: bgImage ? '#000' : '#111',
        display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>
        {bgImage && (
          <>
            <img src={bgImage} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', zIndex:0 }} />
            <Box sx={{ position:'absolute', inset:0, bgcolor:'rgba(0,0,0,0.2)', zIndex:1 }} />
          </>
        )}
        <Box sx={{ flex:1, display:'flex', overflow:'hidden', position:'relative', zIndex:2,
          gap: breakout ? 0 : `${gap}px`, p: breakout ? 0 : `${gap}px`, pb: 0,
          transition:'padding 0.4s ease, gap 0.4s ease' }}>
          <Box sx={{ flex: breakout ? 10 : 7, display:'flex', flexDirection:'column',
            gap: breakout ? 0 : `${gap}px`, transition:'flex 0.4s ease, gap 0.4s ease' }}>
            <Box sx={{ flex:1, position:'relative',
              borderRadius: breakout ? 0 : rad, overflow:'hidden',
              bgcolor: paneBg, backdropFilter: paneBackdrop,
              transition:'border-radius 0.4s ease',
              ...(panelStyle === 'classic' && !breakout && { borderRight:'2px solid #1a1a1a', borderBottom:'2px solid #1a1a1a' }) }}>
              <ZonePlayer slides={slides} zone="HUVUDANSLAG" companyLogo={companyLogo} companyId={display?.companyId} onCurrentSlide={setHuvudSlide} />
            </Box>
            {!breakout && <SidfotRow height={180} />}
          </Box>
          <Box sx={{ flex: breakout ? 0 : 3, position:'relative', overflow:'hidden', minWidth:0,
            transition:'flex 0.4s ease' }}>
            <SidoPanel />
          </Box>
        </Box>
        {!breakout && <FooterBar />}
      </Box>
    );
  }

  // Bottom-bar layout: HUVUD full width + SIDFOT strip at bottom
  if (displayLayout === 'bottom-bar') {
    return (
      <Box sx={{ width:'100vw', height:'100vh', bgcolor: bgImage ? '#000' : '#111',
        display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>
        {bgImage && (
          <>
            <img src={bgImage} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', zIndex:0 }} />
            <Box sx={{ position:'absolute', inset:0, bgcolor:'rgba(0,0,0,0.2)', zIndex:1 }} />
          </>
        )}
        <Box sx={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', position:'relative', zIndex:2,
          gap: breakout ? 0 : `${gap}px`, p: breakout ? 0 : `${gap}px`, pb: 0,
          transition:'padding 0.4s ease, gap 0.4s ease' }}>
          <Box sx={{ flex:1, position:'relative',
            borderRadius: breakout ? 0 : rad, overflow:'hidden',
            bgcolor: paneBg, backdropFilter: paneBackdrop,
            transition:'border-radius 0.4s ease' }}>
            <ZonePlayer slides={slides} zone="HUVUDANSLAG" companyLogo={companyLogo} companyId={display?.companyId} onCurrentSlide={setHuvudSlide} />
          </Box>
          {!breakout && <SidfotRow height={180} />}
        </Box>
        {!breakout && <FooterBar />}
      </Box>
    );
  }

  // Default: fullscreen
  return (
    <Box sx={{ width:'100vw', height:'100vh', bgcolor: bgImage ? '#000' : '#111',
      position:'relative', overflow:'hidden', display:'flex', flexDirection:'column' }}>
      {bgImage && (
        <>
          <img src={bgImage} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', zIndex:0 }} />
          <Box sx={{ position:'absolute', inset:0, bgcolor:'rgba(0,0,0,0.2)', zIndex:1 }} />
        </>
      )}
      <Box sx={{ flex:1, position:'relative', zIndex:2,
        p: panelStyle === 'glass' ? `${gap}px` : 0, pb: 0,
        display:'flex' }}>
        <Box sx={{ flex:1, position:'relative',
          borderRadius: rad, overflow:'hidden',
          bgcolor: paneBg, backdropFilter: paneBackdrop }}>
          <SlideRenderer slide={currentSlide} companyLogo={companyLogo} companyId={display?.companyId} />
        </Box>
      </Box>
      <FooterBar />
      <InstallBanner />
    </Box>
  );
}
